import axios from 'axios';
import { resolveApiBaseUrl } from '../utils/apiBase';
import { withOptionalFallback } from '../utils/optionalApi';
import { normalizeRetellApiPath } from '../utils/retellApiBase';
import { uploadCapabilityAssetsToWorkspace } from '../utils/capabilityAssetUpload';
import { isRetellWorkspaceSetupError } from '../utils/retellProviderErrors';
import { azureVmPaths } from './azureVmPaths';
import { azureVmScaleSetPaths } from './azureVmScaleSetPaths';
import { azureVmScaleSetInstancePaths } from './azureVmScaleSetInstancePaths';
import { azureDiskPaths } from './azureDiskPaths';
import { azureImagePaths } from './azureImagePaths';
import { azureAksPaths } from './azureAksPaths';
import { azureAksAgentPoolPaths } from './azureAksAgentPoolPaths';
import { azureOperationPaths } from './azureOperationPaths';
import { azureReconciliationPaths } from './azureReconciliationPaths';
import { azureSnapshotPaths } from './azureSnapshotPaths';
import { azureNetworkPaths } from './azureNetworkPaths';
import { awsImagePaths } from './awsImagePaths';
import { awsDatabasePaths } from './awsDatabasePaths';
import { awsCachePaths } from './awsCachePaths';
import { awsRoute53Paths } from './awsRoute53Paths';
import { awsInstancePaths } from './awsInstancePaths';
import { awsEksPaths } from './awsEksPaths';
import { awsNetworkPaths } from './awsNetworkPaths';
import { awsOperationPaths } from './awsOperationPaths';
import { awsSnapshotPaths } from './awsSnapshotPaths';
import { awsVolumePaths } from './awsVolumePaths';
import { cloudManagerPaths } from './cloudManagerPaths';
import { marketplaceReviewPaths } from './marketplaceReviewPaths';
import { marketplaceOfferPaths } from './marketplaceOfferPaths';
import { gcpInstancePaths } from './gcpInstancePaths';
import { gcpDiskPaths } from './gcpDiskPaths';
import { gcpFirewallPaths } from './gcpFirewallPaths';
import { gcpGpuCatalogPaths } from './gcpGpuCatalogPaths';
import { gcpImagePaths } from './gcpImagePaths';
import { gcpMachineImagePaths } from './gcpMachineImagePaths';
import { gcpMachineTypeCatalogPaths } from './gcpMachineTypeCatalogPaths';
import { gcpOperationPaths } from './gcpOperationPaths';
import { gcpTemplatePaths } from './gcpTemplatePaths';
import { gcpTpuPaths } from './gcpTpuPaths';
import { buildCreateWorkflowFromActionPlanPayload, buildWorkflowActionPlanExecutionPayload } from './workflowActionPlanPayload';

// Use gateway URL for all API calls in production
const API_BASE_URL = resolveApiBaseUrl(
  import.meta.env.VITE_API_URL || 'http://localhost:3000',
  typeof window !== 'undefined' ? window.location.hostname : ''
);
// AI Resource Manager is now proxied through the gateway
const AI_RESOURCE_MANAGER_URL = import.meta.env.VITE_API_URL;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const aiResourceApi = axios.create({
  baseURL: AI_RESOURCE_MANAGER_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const TENANT_KEY_ROUTE_PREFIXES = ['/compute', '/cloud-manager', '/tenants'];
const OPTIONAL_GET_ROUTE_PATTERNS = [
  /^\/mcps\/tenant$/,
];
export const tenantApiPaths = {
  list: '/auth/tenants',
};

function shouldUseTenantApiKey(url) {
  if (typeof url !== 'string' || !url) return false;
  return TENANT_KEY_ROUTE_PREFIXES.some((prefix) => url.startsWith(prefix));
}

export function isOptionalGetRouteNotFound(method, url, status) {
  if (method !== 'GET' || status !== 404 || typeof url !== 'string') {
    return false;
  }

  return OPTIONAL_GET_ROUTE_PATTERNS.some((pattern) => pattern.test(url));
}

const PLATFORM_HOSTNAMES = new Set([
  'bluebear.ai',
  'www.bluebear.ai',
  'api.bluebear.ai',
  'cloud.bluebear.ai',
  'localhost',
  '127.0.0.1',
]);

export function deriveTenantDomainFromHostname(hostname) {
  const normalized = String(hostname || '').trim().toLowerCase().replace(/\.$/, '');
  if (!normalized || PLATFORM_HOSTNAMES.has(normalized)) return null;
  if (/^marketplace-dev\.[a-z0-9.-]+\.nip\.io$/.test(normalized)) return null;
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(normalized)) return null;
  return normalized;
}

function getTenantSlug(storage = localStorage, currentHostname = '') {
  const override = storage?.getItem?.('tenantSlug');
  if (override) return override;

  const token = storage?.getItem?.('token');
  if (token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (payload.tenant_slug) return payload.tenant_slug;
    } catch {
      // Fall through to the public hostname for pre-auth and malformed tokens.
    }
  }
  return deriveTenantDomainFromHostname(currentHostname);
}

export function resolveRequestAuthHeaders(
  url,
  existingHeaders = {},
  storage = localStorage,
  currentHostname = typeof window !== 'undefined' ? window.location.hostname : ''
) {
  const headers = { ...existingHeaders };
  const tenantApiKey = storage?.getItem?.('tenantApiKey');
  const token = storage?.getItem?.('token');
  const hasExplicitAuth = Boolean(headers.Authorization || headers.authorization || headers['X-API-Key']);

  const slug = getTenantSlug(storage, currentHostname);
  if (slug && !headers['X-Tenant-Domain']) {
    headers['X-Tenant-Domain'] = slug;
  }

  if (hasExplicitAuth) {
    return headers;
  }

  if (tenantApiKey && shouldUseTenantApiKey(url)) {
    delete headers.Authorization;
    delete headers.authorization;
    return {
      ...headers,
      'X-API-Key': tenantApiKey,
    };
  }

  if (token) {
    return {
      ...headers,
      Authorization: `Bearer ${token}`,
    };
  }

  return headers;
}

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    config.headers = resolveRequestAuthHeaders(config.url, config.headers);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors and log responses
api.interceptors.response.use(
  (response) => {
    console.log(`[API] ✓ ${response.config.method?.toUpperCase()} ${response.config.url}`, {
      status: response.status,
      data: response.data,
      headers: response.headers,
      dataType: typeof response.data,
      isArray: Array.isArray(response.data),
      dataKeys: response.data ? Object.keys(response.data) : null
    });
    return response;
  },
  (error) => {
    const method = error.config?.method?.toUpperCase();
    const url = error.config?.url || '';
    const status = error.response?.status;
    const isOptionalMissingRoute = isOptionalGetRouteNotFound(method, url, status);
    const isAuthRoute = typeof url === 'string' && url.startsWith('/auth/');

    if (!isOptionalMissingRoute) {
      console.error(`[API] ✗ ${method} ${url}`, {
        status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
        stack: error.stack
      });
    }

    if (isOptionalMissingRoute) {
      return Promise.reject(error);
    }
    
    if (error.response?.status === 401 && !isAuthRoute) {
      localStorage.removeItem('token');
      localStorage.removeItem('tenantApiKey');
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.assign('/login');
      }
    }
    return Promise.reject(error);
  }
);

// Add auth interceptor for AI Resource Manager API
aiResourceApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

aiResourceApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('tenantApiKey');
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.assign('/login');
      }
    }
    return Promise.reject(error);
  }
);

/* The resolved gateway host, for the few places that need to address the
 * gateway outside axios — the marketplace storefront advertises the manifest
 * and SKILL.md URLs a machine fetches, and those are served at the gateway's
 * root rather than under /api/v1. Exported so nothing has to re-derive it
 * from import.meta.env and get it subtly different. */
export const apiBaseUrl = API_BASE_URL;

// Auth endpoints
export const auth = {
  getConfig: () => api.get('/auth/config'),
  login: (credentials) => api.post('/auth/login', credentials),
  googleLogin: (payload) => api.post('/auth/google', payload),
  register: (userData) => api.post('/auth/register', userData),
  forgotPassword: (payload) => api.post('/auth/forgot-password', payload),
  resetPassword: (payload) => api.post('/auth/reset-password', payload),
  verifyEmail: (payload) => api.post('/auth/verify-email', payload),
  resendVerification: (payload) => api.post('/auth/resend-verification', payload),
  me: () => api.get('/auth/me'),
  getModelRouting: () => api.get('/auth/me/model-routing'),
  updateModelRouting: (payload) => api.put('/auth/me/model-routing', payload),
  refresh: () => api.post('/auth/refresh'),
};

// Waitlist endpoints (public signup + admin)
export const waitlist = {
  signup: (payload) => api.post('/waitlist', payload),
  list: (params) => api.get('/waitlist', { params }),
  approve: (waitlistId) => api.post(`/waitlist/${waitlistId}/approve`),
};

// Pricing endpoints
export const pricing = {
  getAllPrices: (params) => api.get('/pricing/prices', { params }),
  getBestPrice: (params) => api.get('/pricing/best', { params }),
  getSpotPrices: () => api.get('/pricing/spot'),
  getPredictions: (params) => api.get('/pricing/predictions', { params }),
};

// Product catalog endpoints
export const products = {
  getCatalog: () => api.get('/products/catalog'),
};

// Cloud Manager endpoints
export const cloudManager = {
  listComputeLinks: () => api.get(cloudManagerPaths.computeLinks),
  getSizingCatalog: () => withOptionalFallback(() => api.get(cloudManagerPaths.sizingCatalog), { catalog: {}, version: 0 }),
  updateSizingCatalog: (payload) => api.put(cloudManagerPaths.sizingCatalog, payload),
  getSizingCatalogVersions: () => withOptionalFallback(() => api.get(cloudManagerPaths.sizingCatalogVersions), { versions: [] }),
  getSizingCatalogVersion: (version) => api.get(cloudManagerPaths.sizingCatalogVersion(version)),
  createComputeLink: (payload) => api.post(cloudManagerPaths.computeLinks, payload),
  validateComputeLink: (token) =>
    api.get(`${cloudManagerPaths.computeLinks}/validate`, { params: { token } }),
  getComputeContractStatus: (token) =>
    api.get('/compute/contract-status', { params: { token } }),
  refreshComputeContract: (token) =>
    api.post('/compute/contract-refresh', { token }),
  deleteComputeLink: (id) => api.delete(`${cloudManagerPaths.computeLinks}/${id}`),
  submitRequest: (payload) => api.post(cloudManagerPaths.requests, payload),
  submitAgentBundle: (payload) => api.post(cloudManagerPaths.agentBundles, payload),
  listRequests: () => api.get(cloudManagerPaths.requests),
  listAgentBundles: () => api.get(cloudManagerPaths.agentBundles),
};

// Deployment endpoints
export const deployments = {
  analyze: (gitUrl) => api.post('/deploy/analyze', { gitUrl }),
  quickDeploy: (payload) => api.post('/deploy/quick-deploy', payload),
  list: () => withOptionalFallback(() => api.get('/deployments'), []),
  get: (id) => api.get(`/deployments/${id}`),
  getLogs: (id) => api.get(`/deployments/${id}/logs`),
  terminate: (id) => api.delete(`/deployments/${id}`),
};

// Trigger endpoints
export const triggers = {
  generate: (params) => api.post('/triggers/generate', params),
  list: () => api.get('/triggers'),
  revoke: (id) => api.delete(`/triggers/${id}`),
};

// AI Resource Manager - Access Management endpoints
export const accessManagement = {
  // Natural language access management
  manage: (request, cloudAccountId, context) => 
    api.post('/access/manage', { request, cloudAccountId, context }),
  
  // Get access recommendations
  getRecommendations: (params) => 
    api.post('/access/recommendations', params),
  
  // Audit access changes
  getAudit: (cloudAccountId, params) => 
    api.get(`/access/audit/${cloudAccountId}`, { params }),
  
  // Get orchestrator statistics
  getStats: () => 
    api.get('/access/stats'),
  
  // Create structured access request
  createRequest: (params) => 
    api.post('/access/request', params),
  
  // List access for a resource
  listAccess: (cloudAccountId, resourceId) => 
    api.get(`/access/${cloudAccountId}/resources/${resourceId}/access`),
};

// AI Resource Manager - Cloud Accounts endpoints
export const cloudAccounts = {
  list: async () => {
    const response = await api.get('/compute/cloud-account-connections');
    const payload = response?.data;
    const connections = Array.isArray(payload?.connections) ? payload.connections : [];
    return connections.map((connection) => ({
      id: connection.id,
      provider: connection.provider,
      name: connection.account_label || connection.accountLabel || connection.account_identifier || connection.accountIdentifier || `${String(connection.provider || 'cloud').toUpperCase()} Connection`,
      account_identifier: connection.accountIdentifier || connection.account_identifier || null,
      account_scope: connection.accountScope || connection.account_scope || {},
      status: connection.status === 'connected' ? 'active' : (connection.status || 'pending'),
      created_at: connection.createdAt || connection.created_at || null,
      last_tested: connection.lastValidatedAt || connection.last_validated_at || null,
      connection_mode: connection.connection_mode || connection.connectionMode || null,
      auth_method: connection.auth_method || connection.authMethod || null,
      raw: connection,
    }));
  },
  get: (id) => api.get(`/cloud-accounts/${id}`).then(r => r.data),
  listDigitalOceanInventory: (id) => api.get(`/compute/cloud-account-connections/${id}/digitalocean/inventory`).then(r => r.data),
  getDigitalOceanDropletCatalog: id => api.get(`/compute/cloud-account-connections/${id}/digitalocean/droplet-catalog`).then(r => r.data),
  listDigitalOceanSizes: id => api.get(`/compute/cloud-account-connections/${id}/digitalocean/sizes`).then(r => r.data),
  createDigitalOceanDroplet: (id, body) => api.post(`/compute/cloud-account-connections/${id}/digitalocean/droplets`, { ...body, confirmed: true }).then(r => r.data),
  listDigitalOceanDropletActions: (id, dropletId) => api.get(`/compute/cloud-account-connections/${id}/digitalocean/droplets/${encodeURIComponent(dropletId)}/actions`).then(r => r.data),
  runDigitalOceanDropletAction: (id, dropletId, action, options = {}) => api.post(`/compute/cloud-account-connections/${id}/digitalocean/droplets/${encodeURIComponent(dropletId)}/actions`, { action, confirmed: true, ...options }).then(r => r.data),
  attachDigitalOceanWorkspace: (id, dropletId, body) => api.post(`/compute/cloud-account-connections/${id}/digitalocean/droplets/${encodeURIComponent(dropletId)}/workspace-attachment`, { ...body, confirmed: true }).then(r => r.data),
  replaceDigitalOceanToken: (id, token) => api.put(`/compute/cloud-account-connections/${id}/digitalocean/token`, { token }).then(r => r.data),
  create: (account) =>
    api.post('/compute/cloud-account-connections', {
      provider: account.provider,
      name: account.name,
      credentials: account.credentials || {},
    }).then((r) => r.data?.connection || r.data),
  update: (id, updates) => api.put(`/cloud-accounts/${id}`, updates).then(r => r.data),
  delete: (id) => api.delete(`/compute/cloud-account-connections/${id}`).then(r => r.data),
  testConnection: ({ id }) =>
    api.get(`/compute/cloud-account-connections/${id}/activation-status`).then(r => r.data),
  getInventory: (id) =>
    api.get(`/compute/cloud-account-connections/${id}/inventory`).then(r => r.data),
  getAzureReconciliation: (id) =>
    api.get(azureReconciliationPaths.get(id)).then(r => r.data),
  updateAzureMutationPolicy: (id, deleteAllowed, confirmation) =>
    api.patch(`/compute/cloud-account-connections/${id}/azure/mutation-policy`, {
      deleteAllowed,
      confirmation,
    }).then(r => r.data),
  listAzureVms: (id) =>
    api.get(azureVmPaths.list(id)).then(r => r.data),
  createAzureVm: (id, payload) =>
    api.post(azureVmPaths.create(id), payload).then(r => r.data),
  listAzureVmSizes: (id, location) =>
    api.get(azureVmPaths.sizes(id, location)).then(r => r.data),
  getAzureVm: (id, resourceGroup, name) =>
    api.get(azureVmPaths.detail(id, resourceGroup, name)).then(r => r.data),
  deleteAzureVm: (id, resourceGroup, name, confirmationName) =>
    api.delete(azureVmPaths.detail(id, resourceGroup, name), {
      data: { confirmationName },
    }).then(r => r.data),
  startAzureVm: (id, resourceGroup, name) =>
    api.post(azureVmPaths.action(id, resourceGroup, name, 'start')).then(r => r.data),
  stopAzureVm: (id, resourceGroup, name) =>
    api.post(azureVmPaths.action(id, resourceGroup, name, 'powerOff')).then(r => r.data),
  deallocateAzureVm: (id, resourceGroup, name) =>
    api.post(azureVmPaths.action(id, resourceGroup, name, 'deallocate')).then(r => r.data),
  restartAzureVm: (id, resourceGroup, name) =>
    api.post(azureVmPaths.action(id, resourceGroup, name, 'restart')).then(r => r.data),
  updateAzureVm: (id, resourceGroup, name, payload) =>
    api.patch(azureVmPaths.detail(id, resourceGroup, name), payload).then(r => r.data),
  attachAzureVmDisk: (id, resourceGroup, name, payload) =>
    api.post(azureVmPaths.attachDisk(id, resourceGroup, name), payload).then(r => r.data),
  detachAzureVmDisk: (id, resourceGroup, name, payload) =>
    api.post(azureVmPaths.detachDisk(id, resourceGroup, name), payload).then(r => r.data),
  listAzureVmScaleSets: (id) =>
    api.get(azureVmScaleSetPaths.list(id)).then(r => r.data),
  createAzureVmScaleSet: (id, payload) =>
    api.post(azureVmScaleSetPaths.create(id), payload).then(r => r.data),
  getAzureVmScaleSet: (id, resourceGroup, name) =>
    api.get(azureVmScaleSetPaths.detail(id, resourceGroup, name)).then(r => r.data),
  updateAzureVmScaleSet: (id, resourceGroup, name, payload) =>
    api.patch(azureVmScaleSetPaths.detail(id, resourceGroup, name), payload).then(r => r.data),
  deleteAzureVmScaleSet: (id, resourceGroup, name) =>
    api.delete(azureVmScaleSetPaths.detail(id, resourceGroup, name)).then(r => r.data),
  runAzureVmScaleSetAction: (id, resourceGroup, name, action, payload = {}) =>
    api.post(azureVmScaleSetPaths.action(id, resourceGroup, name, action), payload).then(r => r.data),
  listAzureVmScaleSetInstances: (id, resourceGroup, name) =>
    api.get(azureVmScaleSetInstancePaths.list(id, resourceGroup, name)).then(r => r.data),
  getAzureVmScaleSetInstance: (id, resourceGroup, name, instanceId) =>
    api.get(azureVmScaleSetInstancePaths.detail(id, resourceGroup, name, instanceId)).then(r => r.data),
  runAzureVmScaleSetInstanceAction: (id, resourceGroup, name, instanceId, action) =>
    api.post(azureVmScaleSetInstancePaths.action(id, resourceGroup, name, instanceId, action), {}).then(r => r.data),
  listAzureAksClusters: (id) =>
    api.get(azureAksPaths.list(id)).then(r => r.data),
  createAzureAksCluster: (id, payload) =>
    api.post(azureAksPaths.create(id), payload).then(r => r.data),
  getAzureAksCluster: (id, resourceGroup, name) =>
    api.get(azureAksPaths.detail(id, resourceGroup, name)).then(r => r.data),
  listAzureAksVmSizes: (id, location, filters = {}) =>
    api.get(azureAksPaths.vmSizes(id, location, filters)).then(r => r.data),
  getAzureAksClusterUpgradeProfile: (id, resourceGroup, name) =>
    api.get(azureAksPaths.upgradeProfile(id, resourceGroup, name)).then(r => r.data),
  updateAzureAksCluster: (id, resourceGroup, name, payload) =>
    api.patch(azureAksPaths.detail(id, resourceGroup, name), payload).then(r => r.data),
  deleteAzureAksCluster: (id, resourceGroup, name) =>
    api.delete(azureAksPaths.detail(id, resourceGroup, name)).then(r => r.data),
  runAzureAksClusterAction: (id, resourceGroup, name, action) =>
    api.post(azureAksPaths.action(id, resourceGroup, name, action), {}).then(r => r.data),
  listAzureAksAgentPools: (id, resourceGroup, clusterName) =>
    api.get(azureAksAgentPoolPaths.list(id, resourceGroup, clusterName)).then(r => r.data),
  createAzureAksAgentPool: (id, resourceGroup, clusterName, payload) =>
    api.post(azureAksAgentPoolPaths.create(id, resourceGroup, clusterName), payload).then(r => r.data),
  getAzureAksAgentPool: (id, resourceGroup, clusterName, agentPoolName) =>
    api.get(azureAksAgentPoolPaths.detail(id, resourceGroup, clusterName, agentPoolName)).then(r => r.data),
  updateAzureAksAgentPool: (id, resourceGroup, clusterName, agentPoolName, payload) =>
    api.patch(azureAksAgentPoolPaths.detail(id, resourceGroup, clusterName, agentPoolName), payload).then(r => r.data),
  deleteAzureAksAgentPool: (id, resourceGroup, clusterName, agentPoolName) =>
    api.delete(azureAksAgentPoolPaths.detail(id, resourceGroup, clusterName, agentPoolName)).then(r => r.data),
  listAzureDisks: (id) =>
    api.get(azureDiskPaths.list(id)).then(r => r.data),
  createAzureDisk: (id, payload) =>
    api.post(azureDiskPaths.create(id), payload).then(r => r.data),
  getAzureDisk: (id, resourceGroup, name) =>
    api.get(azureDiskPaths.detail(id, resourceGroup, name)).then(r => r.data),
  updateAzureDisk: (id, resourceGroup, name, payload) =>
    api.patch(azureDiskPaths.detail(id, resourceGroup, name), payload).then(r => r.data),
  listAzureImages: (id) =>
    api.get(azureImagePaths.list(id)).then(r => r.data),
  createAzureImage: (id, payload) =>
    api.post(azureImagePaths.create(id), payload).then(r => r.data),
  getAzureImage: (id, resourceGroup, name) =>
    api.get(azureImagePaths.detail(id, resourceGroup, name)).then(r => r.data),
  deleteAzureImage: (id, resourceGroup, name) =>
    api.delete(azureImagePaths.detail(id, resourceGroup, name)).then(r => r.data),
  listAzureSnapshots: (id) =>
    api.get(azureSnapshotPaths.list(id)).then(r => r.data),
  createAzureSnapshot: (id, payload) =>
    api.post(azureSnapshotPaths.create(id), payload).then(r => r.data),
  getAzureSnapshot: (id, resourceGroup, name) =>
    api.get(azureSnapshotPaths.detail(id, resourceGroup, name)).then(r => r.data),
  deleteAzureSnapshot: (id, resourceGroup, name) =>
    api.delete(azureSnapshotPaths.detail(id, resourceGroup, name)).then(r => r.data),
  listAzureNetworkInterfaces: (id) =>
    api.get(azureNetworkPaths.networkInterfaces(id)).then(r => r.data),
  createAzureNetworkInterface: (id, payload) =>
    api.post(azureNetworkPaths.networkInterfaces(id), payload).then(r => r.data),
  updateAzureNetworkInterface: (id, resourceGroup, name, payload) =>
    api.patch(azureNetworkPaths.networkInterfaceDetail(id, resourceGroup, name), payload).then(r => r.data),
  deleteAzureNetworkInterface: (id, resourceGroup, name) =>
    api.delete(azureNetworkPaths.networkInterfaceDetail(id, resourceGroup, name)).then(r => r.data),
  listAzurePublicIps: (id) =>
    api.get(azureNetworkPaths.publicIps(id)).then(r => r.data),
  createAzurePublicIp: (id, payload) =>
    api.post(azureNetworkPaths.publicIps(id), payload).then(r => r.data),
  deleteAzurePublicIp: (id, resourceGroup, name) =>
    api.delete(azureNetworkPaths.publicIpDetail(id, resourceGroup, name)).then(r => r.data),
  listAzureVirtualNetworks: (id) =>
    api.get(azureNetworkPaths.virtualNetworks(id)).then(r => r.data),
  createAzureVirtualNetwork: (id, payload) =>
    api.post(azureNetworkPaths.virtualNetworks(id), payload).then(r => r.data),
  deleteAzureVirtualNetwork: (id, resourceGroup, name) =>
    api.delete(azureNetworkPaths.virtualNetworkDetail(id, resourceGroup, name)).then(r => r.data),
  createAzureSubnet: (id, resourceGroup, name, payload) =>
    api.post(azureNetworkPaths.virtualNetworkSubnets(id, resourceGroup, name), payload).then(r => r.data),
  updateAzureSubnet: (id, resourceGroup, virtualNetworkName, name, payload) =>
    api.patch(azureNetworkPaths.virtualNetworkSubnetDetail(id, resourceGroup, virtualNetworkName, name), payload).then(r => r.data),
  deleteAzureSubnet: (id, resourceGroup, virtualNetworkName, name) =>
    api.delete(azureNetworkPaths.virtualNetworkSubnetDetail(id, resourceGroup, virtualNetworkName, name)).then(r => r.data),
  listAzureNetworkSecurityGroups: (id) =>
    api.get(azureNetworkPaths.networkSecurityGroups(id)).then(r => r.data),
  createAzureNetworkSecurityGroup: (id, payload) =>
    api.post(azureNetworkPaths.networkSecurityGroups(id), payload).then(r => r.data),
  deleteAzureNetworkSecurityGroup: (id, resourceGroup, name) =>
    api.delete(azureNetworkPaths.networkSecurityGroupDetail(id, resourceGroup, name)).then(r => r.data),
  createAzureNetworkSecurityGroupRule: (id, resourceGroup, name, payload) =>
    api.post(azureNetworkPaths.networkSecurityGroupRules(id, resourceGroup, name), payload).then(r => r.data),
  deleteAzureNetworkSecurityGroupRule: (id, resourceGroup, name, ruleName) =>
    api.delete(azureNetworkPaths.networkSecurityGroupRuleDetail(id, resourceGroup, name, ruleName)).then(r => r.data),
  listAzureOperationHistory: (id, options = {}) =>
    api.get(azureOperationPaths.list(id), {
      params: options.refresh ? { refresh: true } : undefined,
    }).then(r => r.data),
  listAwsInstances: (id) =>
    api.get(awsInstancePaths.list(id)).then(r => r.data),
  listAwsDatabases: (id) =>
    api.get(awsDatabasePaths.list(id)).then(r => r.data),
  createAwsDatabase: (id, payload) =>
    api.post(awsDatabasePaths.create(id), payload).then(r => r.data),
  listAwsCaches: (id, options = {}) =>
    api.get(awsCachePaths.list(id), { params: options }).then(r => r.data),
  listAwsCacheReplicationGroups: (id, options = {}) =>
    api.get(awsCachePaths.replicationGroups(id), { params: options }).then(r => r.data),
  createAwsCacheReplicationGroup: (id, payload) =>
    api.post(awsCachePaths.createReplicationGroup(id), payload).then(r => r.data),
  createAwsCache: (id, payload) =>
    api.post(awsCachePaths.create(id), payload).then(r => r.data),
  getAwsCache: (id, region, cacheClusterId) =>
    api.get(awsCachePaths.detail(id, region, cacheClusterId)).then(r => r.data),
  getAwsCacheReplicationGroup: (id, region, replicationGroupId) =>
    api.get(awsCachePaths.replicationGroupDetail(id, region, replicationGroupId)).then(r => r.data),
  failoverAwsCacheReplicationGroup: (id, region, replicationGroupId, payload = {}) =>
    api.post(awsCachePaths.failoverReplicationGroup(id, region, replicationGroupId), payload).then(r => r.data),
  updateAwsCacheReplicationGroup: (id, region, replicationGroupId, payload) =>
    api.put(awsCachePaths.updateReplicationGroup(id, region, replicationGroupId), payload).then(r => r.data),
  deleteAwsCacheReplicationGroup: (id, region, replicationGroupId, options = {}) =>
    api.delete(awsCachePaths.deleteReplicationGroup(id, region, replicationGroupId), { params: options }).then(r => r.data),
  updateAwsCache: (id, region, cacheClusterId, payload) =>
    api.put(awsCachePaths.update(id, region, cacheClusterId), payload).then(r => r.data),
  rebootAwsCache: (id, region, cacheClusterId, payload = {}) =>
    api.post(awsCachePaths.reboot(id, region, cacheClusterId), payload).then(r => r.data),
  deleteAwsCache: (id, region, cacheClusterId) =>
    api.delete(awsCachePaths.delete(id, region, cacheClusterId)).then(r => r.data),
  listAwsHostedZones: (id) =>
    api.get(awsRoute53Paths.zones(id)).then(r => r.data),
  listAwsHealthChecks: (id) =>
    api.get(awsRoute53Paths.healthChecks(id)).then(r => r.data),
  createAwsHealthCheck: (id, payload) =>
    api.post(awsRoute53Paths.healthChecks(id), payload).then(r => r.data),
  getAwsHostedZone: (id, hostedZoneId) =>
    api.get(awsRoute53Paths.zoneDetail(id, hostedZoneId)).then(r => r.data),
  getAwsHealthCheck: (id, healthCheckId) =>
    api.get(awsRoute53Paths.healthCheckDetail(id, healthCheckId)).then(r => r.data),
  updateAwsHealthCheck: (id, healthCheckId, payload) =>
    api.put(awsRoute53Paths.healthCheckDetail(id, healthCheckId), payload).then(r => r.data),
  deleteAwsHealthCheck: (id, healthCheckId) =>
    api.delete(awsRoute53Paths.deleteHealthCheck(id, healthCheckId)).then(r => r.data),
  listAwsRecordSets: (id, hostedZoneId) =>
    api.get(awsRoute53Paths.recordSets(id, hostedZoneId)).then(r => r.data),
  upsertAwsRecordSet: (id, hostedZoneId, payload) =>
    api.post(awsRoute53Paths.recordSets(id, hostedZoneId), payload).then(r => r.data),
  deleteAwsRecordSet: (id, hostedZoneId, params) =>
    api.delete(awsRoute53Paths.deleteRecordSet(id, hostedZoneId, params)).then(r => r.data),
  listAwsDatabaseSnapshots: (id, options = {}) =>
    api.get(awsDatabasePaths.snapshots(id), { params: options }).then(r => r.data),
  getAwsDatabaseSnapshot: (id, region, dbSnapshotIdentifier) =>
    api.get(awsDatabasePaths.snapshotDetail(id, region, dbSnapshotIdentifier)).then(r => r.data),
  deleteAwsDatabaseSnapshot: (id, region, dbSnapshotIdentifier) =>
    api.delete(awsDatabasePaths.snapshotDetail(id, region, dbSnapshotIdentifier)).then(r => r.data),
  getAwsDatabase: (id, region, dbInstanceIdentifier) =>
    api.get(awsDatabasePaths.detail(id, region, dbInstanceIdentifier)).then(r => r.data),
  deleteAwsDatabase: (id, region, dbInstanceIdentifier, options = {}) =>
    api.delete(awsDatabasePaths.delete(id, region, dbInstanceIdentifier), { params: options }).then(r => r.data),
  updateAwsDatabase: (id, region, dbInstanceIdentifier, payload) =>
    api.put(awsDatabasePaths.update(id, region, dbInstanceIdentifier), payload).then(r => r.data),
  createAwsDatabaseReadReplica: (id, region, dbInstanceIdentifier, payload) =>
    api.post(awsDatabasePaths.createReadReplica(id, region, dbInstanceIdentifier), payload).then(r => r.data),
  createAwsDatabaseSnapshot: (id, region, dbInstanceIdentifier, payload = {}) =>
    api.post(awsDatabasePaths.createSnapshot(id, region, dbInstanceIdentifier), payload).then(r => r.data),
  restoreAwsDatabaseSnapshot: (id, region, dbSnapshotIdentifier, payload) =>
    api.post(awsDatabasePaths.restoreSnapshot(id, region, dbSnapshotIdentifier), payload).then(r => r.data),
  startAwsDatabase: (id, region, dbInstanceIdentifier) =>
    api.post(awsDatabasePaths.start(id, region, dbInstanceIdentifier)).then(r => r.data),
  stopAwsDatabase: (id, region, dbInstanceIdentifier) =>
    api.post(awsDatabasePaths.stop(id, region, dbInstanceIdentifier)).then(r => r.data),
  rebootAwsDatabase: (id, region, dbInstanceIdentifier) =>
    api.post(awsDatabasePaths.reboot(id, region, dbInstanceIdentifier)).then(r => r.data),
  listAwsEksClusters: (id) =>
    api.get(awsEksPaths.list(id)).then(r => r.data),
  listAwsSecurityGroups: (id) =>
    api.get(awsNetworkPaths.securityGroups(id)).then(r => r.data),
  getAwsSecurityGroup: (id, region, groupId) =>
    api.get(awsNetworkPaths.securityGroupDetail(id, region, groupId)).then(r => r.data),
  listAwsVpcs: (id) =>
    api.get(awsNetworkPaths.vpcs(id)).then(r => r.data),
  getAwsVpc: (id, region, vpcId) =>
    api.get(awsNetworkPaths.vpcDetail(id, region, vpcId)).then(r => r.data),
  listAwsSubnets: (id) =>
    api.get(awsNetworkPaths.subnets(id)).then(r => r.data),
  getAwsSubnet: (id, region, subnetId) =>
    api.get(awsNetworkPaths.subnetDetail(id, region, subnetId)).then(r => r.data),
  listAwsRouteTables: (id) =>
    api.get(awsNetworkPaths.routeTables(id)).then(r => r.data),
  getAwsRouteTable: (id, region, routeTableId) =>
    api.get(awsNetworkPaths.routeTableDetail(id, region, routeTableId)).then(r => r.data),
  listAwsInternetGateways: (id) =>
    api.get(awsNetworkPaths.internetGateways(id)).then(r => r.data),
  getAwsInternetGateway: (id, region, internetGatewayId) =>
    api.get(awsNetworkPaths.internetGatewayDetail(id, region, internetGatewayId)).then(r => r.data),
  listAwsNatGateways: (id) =>
    api.get(awsNetworkPaths.natGateways(id)).then(r => r.data),
  getAwsNatGateway: (id, region, natGatewayId) =>
    api.get(awsNetworkPaths.natGatewayDetail(id, region, natGatewayId)).then(r => r.data),
  listAwsNetworkAcls: (id) =>
    api.get(awsNetworkPaths.networkAcls(id)).then(r => r.data),
  getAwsNetworkAcl: (id, region, networkAclId) =>
    api.get(awsNetworkPaths.networkAclDetail(id, region, networkAclId)).then(r => r.data),
  listAwsVpcEndpoints: (id) =>
    api.get(awsNetworkPaths.vpcEndpoints(id)).then(r => r.data),
  getAwsVpcEndpoint: (id, region, vpcEndpointId) =>
    api.get(awsNetworkPaths.vpcEndpointDetail(id, region, vpcEndpointId)).then(r => r.data),
  listAwsTransitGateways: (id) =>
    api.get(awsNetworkPaths.transitGateways(id)).then(r => r.data),
  getAwsTransitGateway: (id, region, transitGatewayId) =>
    api.get(awsNetworkPaths.transitGatewayDetail(id, region, transitGatewayId)).then(r => r.data),
  listAwsVpnGateways: (id) =>
    api.get(awsNetworkPaths.vpnGateways(id)).then(r => r.data),
  getAwsVpnGateway: (id, region, vpnGatewayId) =>
    api.get(awsNetworkPaths.vpnGatewayDetail(id, region, vpnGatewayId)).then(r => r.data),
  listAwsCustomerGateways: (id) =>
    api.get(awsNetworkPaths.customerGateways(id)).then(r => r.data),
  getAwsCustomerGateway: (id, region, customerGatewayId) =>
    api.get(awsNetworkPaths.customerGatewayDetail(id, region, customerGatewayId)).then(r => r.data),
  listAwsVpnConnections: (id) =>
    api.get(awsNetworkPaths.vpnConnections(id)).then(r => r.data),
  getAwsVpnConnection: (id, region, vpnConnectionId) =>
    api.get(awsNetworkPaths.vpnConnectionDetail(id, region, vpnConnectionId)).then(r => r.data),
  listAwsLoadBalancers: (id) =>
    api.get(awsNetworkPaths.loadBalancers(id)).then(r => r.data),
  createAwsLoadBalancer: (id, payload) =>
    api.post(awsNetworkPaths.loadBalancers(id), payload).then(r => r.data),
  getAwsLoadBalancer: (id, region, loadBalancerArn) =>
    api.get(awsNetworkPaths.loadBalancerDetail(id, region, loadBalancerArn)).then(r => r.data),
  updateAwsLoadBalancer: (id, region, loadBalancerArn, payload) =>
    api.put(awsNetworkPaths.loadBalancerDetail(id, region, loadBalancerArn), payload).then(r => r.data),
  deleteAwsLoadBalancer: (id, region, loadBalancerArn) =>
    api.delete(awsNetworkPaths.loadBalancerDetail(id, region, loadBalancerArn)).then(r => r.data),
  listAwsTargetGroups: (id) =>
    api.get(awsNetworkPaths.targetGroups(id)).then(r => r.data),
  createAwsTargetGroup: (id, payload) =>
    api.post(awsNetworkPaths.targetGroups(id), payload).then(r => r.data),
  getAwsTargetGroup: (id, region, targetGroupArn) =>
    api.get(awsNetworkPaths.targetGroupDetail(id, region, targetGroupArn)).then(r => r.data),
  updateAwsTargetGroup: (id, region, targetGroupArn, payload) =>
    api.put(awsNetworkPaths.targetGroupDetail(id, region, targetGroupArn), payload).then(r => r.data),
  deleteAwsTargetGroup: (id, region, targetGroupArn) =>
    api.delete(awsNetworkPaths.targetGroupDetail(id, region, targetGroupArn)).then(r => r.data),
  registerAwsTargetGroupTarget: (id, region, targetGroupArn, payload) =>
    api.post(awsNetworkPaths.targetGroupRegisterTarget(id, region, targetGroupArn), payload).then(r => r.data),
  deregisterAwsTargetGroupTarget: (id, region, targetGroupArn, payload) =>
    api.post(awsNetworkPaths.targetGroupDeregisterTarget(id, region, targetGroupArn), payload).then(r => r.data),
  listAwsListeners: (id) =>
    api.get(awsNetworkPaths.listeners(id)).then(r => r.data),
  createAwsListener: (id, payload) =>
    api.post(awsNetworkPaths.listeners(id), payload).then(r => r.data),
  updateAwsListener: (id, region, listenerArn, payload) =>
    api.put(awsNetworkPaths.listenerDetail(id, region, listenerArn), payload).then(r => r.data),
  getAwsListener: (id, region, listenerArn) =>
    api.get(awsNetworkPaths.listenerDetail(id, region, listenerArn)).then(r => r.data),
  deleteAwsListener: (id, region, listenerArn) =>
    api.delete(awsNetworkPaths.listenerDetail(id, region, listenerArn)).then(r => r.data),
  listAwsListenerRules: (id, region, listenerArn) =>
    api.get(awsNetworkPaths.listenerRules(id), { params: { region, listenerArn } }).then(r => r.data),
  getAwsListenerRule: (id, region, ruleArn) =>
    api.get(awsNetworkPaths.listenerRuleDetail(id, region, ruleArn)).then(r => r.data),
  createAwsListenerRule: (id, payload) =>
    api.post(awsNetworkPaths.listenerRules(id), payload).then(r => r.data),
  updateAwsListenerRule: (id, region, ruleArn, payload) =>
    api.put(awsNetworkPaths.listenerRuleDetail(id, region, ruleArn), payload).then(r => r.data),
  deleteAwsListenerRule: (id, region, ruleArn) =>
    api.delete(awsNetworkPaths.listenerRuleDetail(id, region, ruleArn)).then(r => r.data),
  listAwsTargetHealth: (id, region, targetGroupArn) =>
    api.get(awsNetworkPaths.targetHealth(id), { params: { region, targetGroupArn } }).then(r => r.data),
  getAwsTargetHealth: (id, region, targetGroupArn) =>
    api.get(awsNetworkPaths.targetHealthDetail(id, region, targetGroupArn)).then(r => r.data),
  createAwsEksCluster: (id, payload) =>
    api.post(awsEksPaths.create(id), payload).then(r => r.data),
  getAwsEksCluster: (id, region, clusterName) =>
    api.get(awsEksPaths.detail(id, region, clusterName)).then(r => r.data),
  deleteAwsEksCluster: (id, region, clusterName) =>
    api.delete(awsEksPaths.delete(id, region, clusterName)).then(r => r.data),
  listAwsEksNodeGroups: (id, region, clusterName) =>
    api.get(awsEksPaths.nodeGroups(id, region, clusterName)).then(r => r.data),
  createAwsEksNodeGroup: (id, region, clusterName, payload) =>
    api.post(awsEksPaths.createNodeGroup(id, region, clusterName), payload).then(r => r.data),
  getAwsEksNodeGroup: (id, region, clusterName, nodeGroupName) =>
    api.get(awsEksPaths.nodeGroupDetail(id, region, clusterName, nodeGroupName)).then(r => r.data),
  deleteAwsEksNodeGroup: (id, region, clusterName, nodeGroupName) =>
    api.delete(awsEksPaths.deleteNodeGroup(id, region, clusterName, nodeGroupName)).then(r => r.data),
  updateAwsEksNodeGroupScaling: (id, region, clusterName, nodeGroupName, payload) =>
    api.post(awsEksPaths.updateNodeGroupScaling(id, region, clusterName, nodeGroupName), payload).then(r => r.data),
  createAwsInstance: (id, payload) =>
    api.post(awsInstancePaths.create(id), payload).then(r => r.data),
  getAwsInstance: (id, region, instanceId) =>
    api.get(awsInstancePaths.detail(id, region, instanceId)).then(r => r.data),
  updateAwsInstanceTags: (id, region, instanceId, payload) =>
    api.put(awsInstancePaths.tags(id, region, instanceId), payload).then(r => r.data),
  startAwsInstance: (id, region, instanceId) =>
    api.post(awsInstancePaths.start(id, region, instanceId)).then(r => r.data),
  stopAwsInstance: (id, region, instanceId) =>
    api.post(awsInstancePaths.stop(id, region, instanceId)).then(r => r.data),
  rebootAwsInstance: (id, region, instanceId) =>
    api.post(awsInstancePaths.reboot(id, region, instanceId)).then(r => r.data),
  terminateAwsInstance: (id, region, instanceId) =>
    api.post(awsInstancePaths.terminate(id, region, instanceId)).then(r => r.data),
  listAwsVolumes: (id) =>
    api.get(awsVolumePaths.list(id)).then(r => r.data),
  createAwsVolume: (id, payload) =>
    api.post(awsVolumePaths.create(id), payload).then(r => r.data),
  getAwsVolume: (id, region, volumeId) =>
    api.get(awsVolumePaths.detail(id, region, volumeId)).then(r => r.data),
  deleteAwsVolume: (id, region, volumeId) =>
    api.delete(awsVolumePaths.delete(id, region, volumeId)).then(r => r.data),
  attachAwsVolume: (id, region, volumeId, payload) =>
    api.post(awsVolumePaths.attach(id, region, volumeId), payload).then(r => r.data),
  detachAwsVolume: (id, region, volumeId, payload = {}) =>
    api.post(awsVolumePaths.detach(id, region, volumeId), payload).then(r => r.data),
  resizeAwsVolume: (id, region, volumeId, payload) =>
    api.post(awsVolumePaths.resize(id, region, volumeId), payload).then(r => r.data),
  createAwsVolumeSnapshot: (id, region, volumeId, payload) =>
    api.post(awsVolumePaths.snapshot(id, region, volumeId), payload).then(r => r.data),
  listAwsSnapshots: (id) =>
    api.get(awsSnapshotPaths.list(id)).then(r => r.data),
  getAwsSnapshot: (id, region, snapshotId) =>
    api.get(awsSnapshotPaths.detail(id, region, snapshotId)).then(r => r.data),
  deleteAwsSnapshot: (id, region, snapshotId) =>
    api.delete(awsSnapshotPaths.delete(id, region, snapshotId)).then(r => r.data),
  listAwsImages: (id) =>
    api.get(awsImagePaths.list(id)).then(r => r.data),
  createAwsImage: (id, payload) =>
    api.post(awsImagePaths.create(id), payload).then(r => r.data),
  getAwsImage: (id, region, imageId) =>
    api.get(awsImagePaths.detail(id, region, imageId)).then(r => r.data),
  deleteAwsImage: (id, region, imageId) =>
    api.delete(awsImagePaths.delete(id, region, imageId)).then(r => r.data),
  listAwsOperationHistory: (id, options = {}) =>
    api.get(awsOperationPaths.list(id), {
      params: options.refresh ? { refresh: true } : undefined,
    }).then(r => r.data),
  getAwsOperationStatus: (id, operation) =>
    api.get(awsOperationPaths.detail(id, operation)).then(r => r.data),
  listGcpInstances: (id) =>
    api.get(gcpInstancePaths.list(id)).then(r => r.data),
  getGcpInstance: (id, zone, name) =>
    api.get(gcpInstancePaths.detail(id, zone, name)).then(r => r.data),
  startGcpInstance: (id, zone, name) =>
    api.post(gcpInstancePaths.start(id, zone, name)).then(r => r.data),
  stopGcpInstance: (id, zone, name) =>
    api.post(gcpInstancePaths.stop(id, zone, name)).then(r => r.data),
  suspendGcpInstance: (id, zone, name) =>
    api.post(gcpInstancePaths.suspend(id, zone, name)).then(r => r.data),
  resumeGcpInstance: (id, zone, name) =>
    api.post(gcpInstancePaths.resume(id, zone, name)).then(r => r.data),
  resetGcpInstance: (id, zone, name) =>
    api.post(gcpInstancePaths.reset(id, zone, name)).then(r => r.data),
  diagnoseGcpInstanceRecovery: (id, zone, name) =>
    api.get(gcpInstancePaths.recoveryDiagnostics(id, zone, name)).then(r => r.data),
  resetGcpInstanceForRecovery: (id, zone, name, payload) =>
    api.post(gcpInstancePaths.recoveryReset(id, zone, name), payload).then(r => r.data),
  cleanupGcpInstanceForRecovery: (id, zone, name, payload) =>
    api.post(gcpInstancePaths.recoveryCleanup(id, zone, name), payload).then(r => r.data),
  attachGcpInstanceDisk: (id, zone, name, payload) =>
    api.post(gcpInstancePaths.attachDisk(id, zone, name), payload).then(r => r.data),
  detachGcpInstanceDisk: (id, zone, name, payload) =>
    api.post(gcpInstancePaths.detachDisk(id, zone, name), payload).then(r => r.data),
  setGcpInstanceDeletionProtection: (id, zone, name, payload) =>
    api.post(gcpInstancePaths.deletionProtection(id, zone, name), payload).then(r => r.data),
  updateGcpShieldedInstanceConfig: (id, zone, name, payload) =>
    api.patch(gcpInstancePaths.shieldedInstanceConfig(id, zone, name), payload).then(r => r.data),
  updateGcpShieldedIntegrityPolicy: (id, zone, name, payload) =>
    api.patch(gcpInstancePaths.shieldedIntegrityPolicy(id, zone, name), payload).then(r => r.data),
  createGcpInstance: (id, payload) =>
    api.post(gcpInstancePaths.create(id), payload).then(r => r.data),
  deleteGcpInstance: (id, zone, name) =>
    api.delete(gcpInstancePaths.delete(id, zone, name)).then(r => r.data),
  updateGcpInstance: (id, zone, name, payload) =>
    api.patch(gcpInstancePaths.update(id, zone, name), payload).then(r => r.data),
  listGcpDisks: (id) =>
    api.get(gcpDiskPaths.list(id)).then(r => r.data),
  listGcpSnapshots: (id) =>
    api.get(gcpDiskPaths.snapshots(id)).then(r => r.data),
  getGcpSnapshot: (id, name) =>
    api.get(gcpDiskPaths.snapshotDetail(id, name)).then(r => r.data),
  updateGcpSnapshot: (id, name, payload) =>
    api.patch(gcpDiskPaths.updateSnapshot(id, name), payload).then(r => r.data),
  deleteGcpSnapshot: (id, name) =>
    api.delete(gcpDiskPaths.removeSnapshot(id, name)).then(r => r.data),
  getGcpDisk: (id, scopeType, scope, name) =>
    api.get(gcpDiskPaths.detail(id, scopeType, scope, name)).then(r => r.data),
  createGcpDisk: (id, payload) =>
    api.post(gcpDiskPaths.create(id), payload).then(r => r.data),
  updateGcpDisk: (id, scopeType, scope, name, payload) =>
    api.patch(gcpDiskPaths.update(id, scopeType, scope, name), payload).then(r => r.data),
  deleteGcpDisk: (id, scopeType, scope, name) =>
    api.delete(gcpDiskPaths.remove(id, scopeType, scope, name)).then(r => r.data),
  createGcpDiskSnapshot: (id, scopeType, scope, name, payload) =>
    api.post(gcpDiskPaths.snapshot(id, scopeType, scope, name), payload).then(r => r.data),
  resizeGcpDisk: (id, scopeType, scope, name, payload) =>
    api.post(gcpDiskPaths.resize(id, scopeType, scope, name), payload).then(r => r.data),
  listGcpInstanceTemplates: (id) =>
    api.get(gcpTemplatePaths.list(id)).then(r => r.data),
  getGcpInstanceTemplate: (id, name) =>
    api.get(gcpTemplatePaths.detail(id, name)).then(r => r.data),
  createGcpInstanceTemplate: (id, payload) =>
    api.post(gcpTemplatePaths.create(id), payload).then(r => r.data),
  deleteGcpInstanceTemplate: (id, name) =>
    api.delete(gcpTemplatePaths.remove(id, name)).then(r => r.data),
  listGcpMachineImages: (id) =>
    api.get(gcpMachineImagePaths.list(id)).then(r => r.data),
  getGcpMachineImage: (id, name) =>
    api.get(gcpMachineImagePaths.detail(id, name)).then(r => r.data),
  createGcpMachineImage: (id, payload) =>
    api.post(gcpMachineImagePaths.create(id), payload).then(r => r.data),
  updateGcpMachineImage: (id, name, payload) =>
    api.patch(gcpMachineImagePaths.update(id, name), payload).then(r => r.data),
  deleteGcpMachineImage: (id, name) =>
    api.delete(gcpMachineImagePaths.remove(id, name)).then(r => r.data),
  listGcpImages: (id) =>
    api.get(gcpImagePaths.list(id)).then(r => r.data),
  getGcpImage: (id, name) =>
    api.get(gcpImagePaths.detail(id, name)).then(r => r.data),
  createGcpImage: (id, payload) =>
    api.post(gcpImagePaths.create(id), payload).then(r => r.data),
  updateGcpImage: (id, name, payload) =>
    api.patch(gcpImagePaths.update(id, name), payload).then(r => r.data),
  deleteGcpImage: (id, name) =>
    api.delete(gcpImagePaths.remove(id, name)).then(r => r.data),
  listGcpFirewalls: (id) =>
    api.get(gcpFirewallPaths.list(id)).then(r => r.data),
  getGcpFirewall: (id, name) =>
    api.get(gcpFirewallPaths.detail(id, name)).then(r => r.data),
  createGcpFirewall: (id, payload) =>
    api.post(gcpFirewallPaths.create(id), payload).then(r => r.data),
  updateGcpFirewall: (id, name, payload) =>
    api.patch(gcpFirewallPaths.update(id, name), payload).then(r => r.data),
  deleteGcpFirewall: (id, name) =>
    api.delete(gcpFirewallPaths.remove(id, name)).then(r => r.data),
  listGcpTpus: (id, locations) =>
    api.get(gcpTpuPaths.list(id, locations)).then(r => r.data),
  getGcpGpuCatalog: (id, params) =>
    api.get(gcpGpuCatalogPaths.catalog(id, params)).then(r => r.data),
  getGcpMachineTypeCatalog: (id, params) =>
    api.get(gcpMachineTypeCatalogPaths.catalog(id, params)).then(r => r.data),
  listGcpTpuCatalog: (id, locations) =>
    api.get(gcpTpuPaths.catalog(id, locations)).then(r => r.data),
  getGcpTpu: (id, location, name) =>
    api.get(gcpTpuPaths.detail(id, location, name)).then(r => r.data),
  createGcpTpu: (id, payload) =>
    api.post(gcpTpuPaths.create(id), payload).then(r => r.data),
  startGcpTpu: (id, location, name) =>
    api.post(gcpTpuPaths.start(id, location, name)).then(r => r.data),
  stopGcpTpu: (id, location, name) =>
    api.post(gcpTpuPaths.stop(id, location, name)).then(r => r.data),
  deleteGcpTpu: (id, location, name) =>
    api.delete(gcpTpuPaths.remove(id, location, name)).then(r => r.data),
  listGcpOperationHistory: (id) =>
    api.get(gcpOperationPaths.list(id)).then(r => r.data),
  getGcpOperationStatus: (id, operation) =>
    api.get(gcpOperationPaths.detail(id, operation)).then(r => r.data),
  oauthInitiate: async (provider, payload = {}) => {
    if (!['azure', 'gcp'].includes(provider)) {
      throw new Error('Browser OAuth is currently available only for Azure and Google Cloud. Use manual connection for AWS.');
    }

    const accountIdentifier =
      provider === 'azure'
        ? (payload.subscriptionId || payload.subscription_id || null)
        : (payload.projectId || payload.project_id || null);
    const accountScope =
      provider === 'azure'
        ? {
            subscriptionId: payload.subscriptionId || payload.subscription_id || null,
            tenantId: payload.tenantId || payload.tenant_id || null,
          }
        : {
            projectId: payload.projectId || payload.project_id || null,
          };

    const createResponse = await api.post('/compute/cloud-account-connections', {
      provider,
      connectionMode: 'interactive_user_login',
      authMethod: 'oauth_authorization_code',
      oauthMode: 'managed',
      accountLabel: payload.name || null,
      accountIdentifier,
      accountScope,
    });

    const connection = createResponse.data?.connection || createResponse.data;
    if (!connection?.id) {
      throw new Error('Failed to create cloud account OAuth session');
    }

    const returnTo = payload.returnTo || `${window.location.origin}/cloud-manager`;
    const sessionResponse = await api.post(`/compute/cloud-account-connections/${connection.id}/auth-sessions`, {
      returnTo,
    });
    const session = sessionResponse.data?.session || sessionResponse.data;
    const url = session?.authorizeUrl || session?.authorize_url;

    if (!url) {
      throw new Error('OAuth authorize URL was not returned');
    }

    return {
      connection,
      session,
      url,
    };
  },
  oauthResume: async ({ id, returnTo }) => {
    const sessionResponse = await api.post(`/compute/cloud-account-connections/${id}/auth-sessions`, {
      returnTo: returnTo || `${window.location.origin}/cloud-manager`,
    });
    const session = sessionResponse.data?.session || sessionResponse.data;
    const url = session?.authorizeUrl || session?.authorize_url;

    if (!url) {
      throw new Error('OAuth authorize URL was not returned');
    }

    return {
      session,
      url,
    };
  },
};

// AI Resource Manager - Resources endpoints
export const cloudResources = {
  discover: (cloudAccountId) => api.post(`/resources/discover/${cloudAccountId}`),
  list: (cloudAccountId, params) => api.get(`/resources/${cloudAccountId}`, { params }),
  getDetails: (cloudAccountId, resourceId) => api.get(`/resources/${cloudAccountId}/${resourceId}`),
};

// Helper to get tenant API headers (uses tenantApiKey for tenant API calls)
const getTenantApiHeaders = () => {
  const tenantApiKey = localStorage.getItem('tenantApiKey');
  if (tenantApiKey) {
    return { 'X-API-Key': tenantApiKey };
  }
  // Fallback to user token
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const getUserApiHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Tenant Management endpoints (White-Label)
export const tenants = {
  // ============================================
  // CORE CRUD OPERATIONS
  // ============================================
  list: (params = {}) => api.get(tenantApiPaths.list, { params }),
  get: (tenantId) => api.get(`/tenants/${tenantId}`, { headers: getTenantApiHeaders() }),
  create: (payload) => api.post('/tenants/create', payload, { headers: getTenantApiHeaders() }),
  update: (tenantId, updates) => api.put(`/tenants/${tenantId}`, updates, { headers: getUserApiHeaders() }),
  delete: (tenantId) => api.delete(`/tenants/${tenantId}`, { headers: getTenantApiHeaders() }),
  // Domain mappings (every hostname the tenant's whitelabel answers on).
  listDomains: (tenantId) => api.get(`/tenants/${tenantId}/domains`, { headers: getUserApiHeaders() }),
  addDomain: (tenantId, payload) => api.post(`/tenants/${tenantId}/domains`, payload, { headers: getUserApiHeaders() }),
  removeDomain: (tenantId, domainId) => api.delete(`/tenants/${tenantId}/domains/${domainId}`, { headers: getUserApiHeaders() }),
  getModelRouting: (tenantId) => api.get(`/tenants/${tenantId}/model-routing`, { headers: getUserApiHeaders() }),
  updateModelRouting: (tenantId, payload) => api.put(`/tenants/${tenantId}/model-routing`, payload, { headers: getUserApiHeaders() }),

  // ============================================
  // HIERARCHICAL SETTINGS API (PRIMARY)
  // All settings changes should go through these methods
  // ============================================
  settings: {
    // Get effective settings for tenant (resolves full inheritance chain)
    getEffective: (tenantId, category = null) =>
      api.get(`/tenants/${tenantId}/settings${category ? `?category=${category}` : ''}`),

    // Get settings for specific category
    getCategory: (tenantId, category) =>
      api.get(`/tenants/${tenantId}/settings/${category}`),

    // Update single setting
    update: (tenantId, category, key, value, reason = null) =>
      api.put(`/tenants/${tenantId}/settings/${category}/${key}`, { value, reason }),

    // Reset setting to inherited value
    reset: (tenantId, category, key) =>
      api.delete(`/tenants/${tenantId}/settings/${category}/${key}`),

    // Bulk update category settings
    bulkUpdate: (tenantId, category, settings) =>
      api.put(`/tenants/${tenantId}/settings/${category}`, { settings }),

    // Check if tenant can override a specific setting
    canOverride: (tenantId, category, key) =>
      api.get(`/tenants/${tenantId}/settings/${category}/${key}/can-override`),
  },

  // Infrastructure defaults for Terraform
  infrastructureSettings: (tenantId) =>
    api.get(`/tenants/${tenantId}/infrastructure-settings`, { headers: getTenantApiHeaders() }),
  infrastructureProfiles: (tenantId, params) =>
    api.get(`/tenants/${tenantId}/infrastructure-profiles`, { params, headers: getTenantApiHeaders() }),
  createInfrastructureProfile: (tenantId, payload) =>
    api.post(`/tenants/${tenantId}/infrastructure-profiles`, payload, { headers: getTenantApiHeaders() }),
  updateInfrastructureProfile: (tenantId, profileId, payload) =>
    api.put(`/tenants/${tenantId}/infrastructure-profiles/${profileId}`, payload, { headers: getTenantApiHeaders() }),
  deleteInfrastructureProfile: (tenantId, profileId) =>
    api.delete(`/tenants/${tenantId}/infrastructure-profiles/${profileId}`, { headers: getTenantApiHeaders() }),
  setInfrastructureProfileSelection: (tenantId, payload) =>
    api.put(`/tenants/${tenantId}/infrastructure-profile`, payload, { headers: getTenantApiHeaders() }),

  // ============================================
  // MCP TOGGLE API (DEDICATED - ENSURES HISTORY LOGGING)
  // Use these methods for MCP changes to ensure audit trail
  // ============================================
  mcps: {
    // Get enabled MCPs for tenant
    list: (tenantId) =>
      api.get(`/tenants/${tenantId}/mcps`),

    // Enable an MCP (with history logging)
    enable: (tenantId, mcpSlug, reason = null) =>
      api.post(`/tenants/${tenantId}/mcps/${mcpSlug}`, { reason }),

    // Disable an MCP (with history logging)
    disable: (tenantId, mcpSlug, reason = null) =>
      api.delete(`/tenants/${tenantId}/mcps/${mcpSlug}`, { data: { reason } }),

    // Bulk update MCPs (with history logging)
    bulkUpdate: (tenantId, mcpSlugs, reason = null) =>
      api.put(`/tenants/${tenantId}/mcps`, { mcps: mcpSlugs, reason }),

    // Get MCP toggle history for audit
    getHistory: (tenantId, limit = 50) =>
      api.get(`/tenants/${tenantId}/mcps/history`, { params: { limit } }),
  },

  // ============================================
  // IMPLEMENTATION TOGGLE API
  // ============================================
  implementations: {
    // Get enabled implementations
    list: (tenantId) =>
      api.get(`/tenants/${tenantId}/implementations`),

    // Enable an implementation
    enable: (tenantId, implSlug) =>
      api.post(`/tenants/${tenantId}/implementations/${implSlug}`),

    // Disable an implementation
    disable: (tenantId, implSlug) =>
      api.delete(`/tenants/${tenantId}/implementations/${implSlug}`),

    // Bulk update implementations
    bulkUpdate: (tenantId, implSlugs) =>
      api.put(`/tenants/${tenantId}/implementations`, { implementations: implSlugs }),
  },

  // ============================================
  // USER PREFERENCES (user-level overrides)
  // ============================================
  preferences: {
    get: (tenantId, userId, category = null) =>
      api.get(`/tenants/${tenantId}/users/${userId}/preferences${category ? `?category=${category}` : ''}`),

    update: (tenantId, userId, category, key, value) =>
      api.put(`/tenants/${tenantId}/users/${userId}/preferences/${category}/${key}`, { value }),

    bulkUpdate: (tenantId, userId, preferences) =>
      api.put(`/tenants/${tenantId}/users/${userId}/preferences`, { preferences }),
  },

  // ============================================
  // PLATFORM SETTINGS (admin only)
  // ============================================
  platform: {
    getDefaults: (category = null) =>
      api.get(`/tenants/platform/settings${category ? `?category=${category}` : ''}`),

    updateDefault: (category, key, updates) =>
      api.put(`/tenants/platform/settings/${category}/${key}`, updates),
  },

  // ============================================
  // SUBSCRIPTION PLANS
  // ============================================
  plans: {
    list: () => api.get('/tenants/subscription-plans'),

    apply: (tenantId, planSlug) =>
      api.post(`/tenants/${tenantId}/apply-plan`, { plan_slug: planSlug }),
  },

};

export function normalizeBillingCheckoutPayload(payload = {}) {
  const normalized = { ...payload };

  if (normalized.amountCents == null && normalized.amount_cents != null) {
    normalized.amountCents = normalized.amount_cents;
  }

  if (!normalized.successUrl && normalized.success_url) {
    normalized.successUrl = normalized.success_url;
  }

  if (!normalized.cancelUrl && normalized.cancel_url) {
    normalized.cancelUrl = normalized.cancel_url;
  }

  delete normalized.amount_cents;
  delete normalized.success_url;
  delete normalized.cancel_url;

  return normalized;
}

export function buildBillingCheckoutReturnUrls(origin, pathname) {
  const baseUrl = `${String(origin || '').replace(/\/$/, '')}${pathname}`;
  return {
    successUrl: `${baseUrl}?success=1&session_id={CHECKOUT_SESSION_ID}`,
    cancelUrl: `${baseUrl}?cancelled=1`,
  };
}

// Billing endpoints
export const billing = {
  getBalance: (params) => api.get('/billing/credits/balance', { params }),
  checkout: (payload) => api.post('/billing/checkout', normalizeBillingCheckoutPayload(payload)),
  reconcileCheckout: (sessionId) => api.post(`/billing/checkout/${sessionId}/reconcile`),
  cancelCheckout: (sessionId) => api.post(`/billing/checkout/${sessionId}/cancel`),
  getPayments: (params) => api.get('/billing/payments', { params }),
  getPayment: (id) => api.get(`/billing/payments/${id}`),
  getUsageSummary: (params) => api.get('/billing/usage/summary', { params }),
  getLedger: (params) => api.get('/billing/credits/ledger', { params }),
  getReconciliationHealth: (params) => api.get('/billing/admin/reconciliation-health', { params }),
  getRoutingUsageHealth: (params) => api.get('/billing/admin/routing-usage-health', { params }),
  getModelRoutingAudit: (params) => api.get('/billing/admin/model-routing-audit', { params }),
  getModelRoutingDecisionAudit: (params) => api.get('/billing/admin/model-routing-decision-audit', { params }),
  getModelRoutingEvalResults: (params) => api.get('/billing/admin/model-routing-eval-results', { params }),
  getModelRoutingInsights: (params) => api.get('/billing/admin/model-routing-insights', { params }),
  getModelRoutingOptimizationReadiness: (params) => api.get('/billing/admin/model-routing-optimization-readiness', { params }),
};

export const contact = {
  submit: (payload) => api.post('/contact', payload),
};

export const notifications = {
  listEvents: (params) => api.get('/admin/notifications/events', { params }),
  listMessages: (params) => api.get('/admin/notifications/messages', { params }),
  getAttempts: (messageId) => api.get(`/admin/notifications/messages/${messageId}/attempts`),
  processEvents: (limit = 50) => api.post('/admin/notifications/events/process', { limit }),
  processMessages: (limit = 50) => api.post('/admin/notifications/messages/process', { limit }),
  replayMessage: (messageId) => api.post(`/admin/notifications/messages/${messageId}/replay`),
};

// AI Resource Manager - Chat endpoint
export const chat = {
  send: (message, context = {}) => api.post('/chat', { message, context }),
};

// Models endpoints (AI Platform)
export const models = {
  getTypes: () => api.get('/models/types'),
  createType: (payload) => api.post('/models/types', payload),
  deleteType: (id) => api.delete('/models/types', { data: { id } }),
  getModels: () => api.get('/models/models'),
  createModel: (payload) => api.post('/models/models', payload),
  deleteModel: (id) => api.delete('/models/models', { data: { id } }),
  getKeys: () => api.get('/models/keys'),
  createKey: (payload) => api.post('/models/keys', payload),
  deleteKey: (id) => api.delete(`/models/keys/${id}`),
  rotateKey: (id, apiKey) => api.put(`/models/keys/${id}/rotate`, { apiKey }),
};

// Agents endpoints (AI Platform)
export const agents = {
  list: () => api.get('/agents'),
  create: (payload) => api.post('/agents', payload),
  update: (id, payload) => api.put(`/agents/${id}`, payload),
  getModelRouting: (idOrSlug) => api.get(`/agents/${idOrSlug}/model-routing`),
  updateModelRouting: (idOrSlug, payload) => api.put(`/agents/${idOrSlug}/model-routing`, payload),
  listMcps: () => Promise.resolve({ data: [] }),
  addMcp: (id, payload) => api.post(`/agents/${id}/mcps`, payload),
  removeMcp: (id, mcpId) => api.delete(`/agents/${id}/mcps/${mcpId}`),
  // Agent-owned skills (manifest-backed; materialized per-turn on agent switch).
  listSkills: (id) => api.get(`/agents/${id}/skills`),
  saveSkills: (id, payload) => api.post(`/agents/${id}/skills`, payload),
  deleteSkill: (id, path) => api.delete(`/agents/${id}/skills`, { data: { path } }),
  delete: (id) => api.delete(`/agents/${id}`),
};

// Gateway-owned agent-plan controls. Provider runtimes can report a blocked
// action, but only this tenant-scoped control plane can approve and resume it.
export const agentPlans = {
  getRun: (planId, runId) =>
    api.get(`/agent-plans/${encodeURIComponent(planId)}/runs/${encodeURIComponent(runId)}`),
  resumeRun: (planId, runId, payload) =>
    api.post(`/agent-plans/${encodeURIComponent(planId)}/runs/${encodeURIComponent(runId)}/resume`, payload),
  cancelRun: (planId, runId, payload) =>
    api.post(`/agent-plans/${encodeURIComponent(planId)}/runs/${encodeURIComponent(runId)}/cancel`, payload),
  runExisting: (planId, runId) =>
    api.post(`/agent-plans/${encodeURIComponent(planId)}/run`, { agent_plan_run_id: runId }),
};

export const channels = {
  list: (agentId) => api.get(`/agents/${agentId}/channels`),
  create: (agentId, payload) => api.post(`/agents/${agentId}/channels`, payload),
  setStatus: (agentId, channelId, status) =>
    api.patch(`/agents/${agentId}/channels/${channelId}`, { status }),
  remove: (agentId, channelId) => api.delete(`/agents/${agentId}/channels/${channelId}`),
};

export const mcp = {
  listEnabled: () => withOptionalFallback(() => api.get('/mcps/tenant'), []),
  // All four access layers for one enablement (tenant → orgs → workspaces →
  // user connections) plus can_manage flags. Platform admins may pass
  // { tenant_id } to read another tenant's summary. Whitelabel renders the
  // same contract — keep the shape in sync with its parity test.
  accessSummary: (id, params = {}) =>
    api.get(`/mcps/tenant/${id}/access-summary`, { params }),
  listRegistry: () => api.get('/mcps/registry'),
  // Platform-admin registry CRUD (backend gates with requirePlatformAdmin).
  // updateRegistry carries pricing + meteringEnabled for the Manage Integrations page.
  updateRegistry: (id, payload) => api.put(`/mcps/registry/${id}`, payload),
  listAvailable: () => api.get('/mcps/tenant/available'),
  enable: (payload) => api.post('/mcps/tenant', payload),
  update: (id, payload) => api.put(`/mcps/tenant/${id}`, payload),
  disable: (id) => api.delete(`/mcps/tenant/${id}`),
  testCredentials: (payload) => api.post('/mcps/tenant/test-credentials', payload),
  // Nango multi-tenant connect flow
  nangoConnectSession: (payload) => api.post('/mcps/nango/connect-session', payload),
  nangoSaveConnection: (payload) => api.post('/mcps/nango/connections', payload),
};

// Platform-admin usage analytics (cross-tenant). Backend gates with requirePlatformAdmin.
export const usage = {
  mcpTools: (params) => api.get('/usage/mcp-tools', { params }),
  mcpToolCalls: (params) => api.get('/usage/mcp-tools/calls', { params }),
  // Summed MCP SPEND (actual charged amounts) by MCP — the cost view.
  mcpCost: (params) => api.get('/usage/mcp-cost', { params }),
};

export const retell = {
  listProviderProfiles: () => withOptionalFallback(() => api.get(normalizeRetellApiPath('/voice/retell/provider-profiles')), []),
  getProviderProfile: (id) => api.get(normalizeRetellApiPath(`/voice/retell/provider-profiles/${id}`)),
  createProviderProfile: (payload) => api.post(normalizeRetellApiPath('/voice/retell/provider-profiles'), payload),
  updateProviderProfile: (id, payload) => api.put(normalizeRetellApiPath(`/voice/retell/provider-profiles/${id}`), payload),
  deleteProviderProfile: (id) => api.delete(normalizeRetellApiPath(`/voice/retell/provider-profiles/${id}`)),
  testProviderProfileConnection: (id) => api.post(normalizeRetellApiPath(`/voice/retell/provider-profiles/${id}/test`)),
  listVoices: (id) => api.get(normalizeRetellApiPath(`/voice/retell/provider-profiles/${id}/voices`)),
  listLlms: (id) => api.get(normalizeRetellApiPath(`/voice/retell/provider-profiles/${id}/llms`)),
  listKnowledgeBases: () => Promise.resolve({ data: [] }),
  getKnowledgeBase: () => Promise.resolve({ data: {} }),
  createKnowledgeBase: () => Promise.reject(new Error('Retell knowledge base management is not supported by the current gateway contract')),
  updateKnowledgeBase: () => Promise.reject(new Error('Retell knowledge base management is not supported by the current gateway contract')),
  deleteKnowledgeBase: () => Promise.reject(new Error('Retell knowledge base management is not supported by the current gateway contract')),
  addKnowledgeBaseSources: () => Promise.reject(new Error('Retell knowledge base management is not supported by the current gateway contract')),
  deleteKnowledgeBaseSource: () => Promise.reject(new Error('Retell knowledge base management is not supported by the current gateway contract')),
  listWorkspaces: () => withOptionalFallback(() => api.get(normalizeRetellApiPath('/voice/retell/workspaces')), []),
  createWorkspace: (payload) => api.post(normalizeRetellApiPath('/voice/retell/workspaces'), payload),
  getWorkspaceConfig: (workspaceId) => api.get(normalizeRetellApiPath(`/voice/retell/workspaces/${workspaceId}/config`)),
  updateWorkspaceConfig: (workspaceId, payload) => api.put(normalizeRetellApiPath(`/voice/retell/workspaces/${workspaceId}/config`), payload),
  listWorkspaceVoices: (workspaceId) =>
    withOptionalFallback(
      () => api.get(normalizeRetellApiPath(`/voice/retell/workspaces/${workspaceId}/voices`)),
      [],
      isRetellWorkspaceSetupError
    ),
  listWorkspaceLlms: (workspaceId) =>
    withOptionalFallback(
      () => api.get(normalizeRetellApiPath(`/voice/retell/workspaces/${workspaceId}/llms`)),
      [],
      isRetellWorkspaceSetupError
    ),
  getWorkspaceLlmRecommendation: (workspaceId, modelId) =>
    withOptionalFallback(
      () =>
        api.get(
          normalizeRetellApiPath(
            `/voice/retell/workspaces/${workspaceId}/llm-recommendation?model_id=${encodeURIComponent(modelId)}`
          )
        ),
      null,
      (error) => error?.response?.status === 404 || isRetellWorkspaceSetupError(error)
    ),
  getWorkspaceLlm: (workspaceId, llmId) =>
    api.get(normalizeRetellApiPath(`/voice/retell/workspaces/${workspaceId}/llms/${llmId}`)),
  createWorkspaceLlm: (workspaceId, payload) =>
    api.post(normalizeRetellApiPath(`/voice/retell/workspaces/${workspaceId}/llms`), payload),
  updateWorkspaceLlm: (workspaceId, llmId, payload) =>
    api.put(normalizeRetellApiPath(`/voice/retell/workspaces/${workspaceId}/llms/${llmId}`), payload),
  deleteWorkspaceLlm: (workspaceId, llmId) =>
    api.delete(normalizeRetellApiPath(`/voice/retell/workspaces/${workspaceId}/llms/${llmId}`)),
  listWorkspaceKnowledgeBases: (workspaceId) =>
    withOptionalFallback(
      () =>
        api.get(normalizeRetellApiPath(`/voice/retell/workspaces/${workspaceId}/knowledge-bases`)),
      [],
      isRetellWorkspaceSetupError
    ),
  getWorkspaceKnowledgeBase: (workspaceId, knowledgeBaseId) =>
    api.get(normalizeRetellApiPath(`/voice/retell/workspaces/${workspaceId}/knowledge-bases/${knowledgeBaseId}`)),
  readWorkspaceKnowledgeBaseSource: (workspaceId, knowledgeBaseId, sourceId) =>
    api.get(
      normalizeRetellApiPath(
        `/voice/retell/workspaces/${workspaceId}/knowledge-bases/${knowledgeBaseId}/sources/${sourceId}/read`
      )
    ),
  createWorkspaceKnowledgeBase: (workspaceId, payload) =>
    api.post(normalizeRetellApiPath(`/voice/retell/workspaces/${workspaceId}/knowledge-bases`), payload),
  addWorkspaceKnowledgeBaseSources: (workspaceId, knowledgeBaseId, payload) =>
    api.post(normalizeRetellApiPath(`/voice/retell/workspaces/${workspaceId}/knowledge-bases/${knowledgeBaseId}/sources`), payload),
  deleteWorkspaceKnowledgeBase: (workspaceId, knowledgeBaseId) =>
    api.delete(normalizeRetellApiPath(`/voice/retell/workspaces/${workspaceId}/knowledge-bases/${knowledgeBaseId}`)),
  deleteWorkspaceKnowledgeBaseSource: (workspaceId, knowledgeBaseId, sourceId) =>
    api.delete(
      normalizeRetellApiPath(
        `/voice/retell/workspaces/${workspaceId}/knowledge-bases/${knowledgeBaseId}/sources/${sourceId}`
      )
    ),
  listManagedAgents: () => withOptionalFallback(() => api.get(normalizeRetellApiPath('/voice/retell/agents')), []),
  createManagedAgent: (payload) => api.post(normalizeRetellApiPath('/voice/retell/agents'), payload),
  updateManagedAgent: (id, payload) => api.put(normalizeRetellApiPath(`/voice/retell/agents/${id}`), payload),
  publishManagedAgent: (id) => api.post(normalizeRetellApiPath(`/voice/retell/agents/${id}/publish`)),
  deleteManagedAgent: (id) => api.delete(normalizeRetellApiPath(`/voice/retell/agents/${id}`)),
  listPhoneNumbers: (workspaceId) =>
    withOptionalFallback(
      () => api.get(normalizeRetellApiPath(`/voice/retell/workspaces/${workspaceId}/phone-numbers`)),
      []
    ),
  createPhoneNumber: (workspaceId, payload) =>
    api.post(normalizeRetellApiPath(`/voice/retell/workspaces/${workspaceId}/phone-numbers`), payload),
  updatePhoneNumber: (workspaceId, id, payload) =>
    api.put(normalizeRetellApiPath(`/voice/retell/workspaces/${workspaceId}/phone-numbers/${id}`), payload),
  registerPhoneNumber: (workspaceId, id) =>
    api.post(normalizeRetellApiPath(`/voice/retell/workspaces/${workspaceId}/phone-numbers/${id}/register`)),
  deregisterPhoneNumber: (workspaceId, id) =>
    api.post(normalizeRetellApiPath(`/voice/retell/workspaces/${workspaceId}/phone-numbers/${id}/deregister`)),
  importPhoneNumber: (workspaceId, payload) =>
    api.post(normalizeRetellApiPath(`/voice/retell/workspaces/${workspaceId}/phone-numbers`), payload),
  deletePhoneNumber: (workspaceId, id) =>
    api.delete(normalizeRetellApiPath(`/voice/retell/workspaces/${workspaceId}/phone-numbers/${id}`)),
  listCallSessions: (workspaceId, params) =>
    api.get(normalizeRetellApiPath(`/voice/retell/workspaces/${workspaceId}/call-sessions`), { params }),
  getCallSessionDebug: (workspaceId, id) =>
    api.get(normalizeRetellApiPath(`/voice/retell/workspaces/${workspaceId}/call-sessions/${id}`)),
  listCallEvents: () => Promise.resolve({ data: [] }),
  requestCallSessionHandoff: (id, payload) => api.post(normalizeRetellApiPath(`/voice/retell/call-sessions/${id}/handoff`), payload),
  markCallSessionFallback: (id, payload) => api.post(normalizeRetellApiPath(`/voice/retell/call-sessions/${id}/fallback`), payload),
};

// Conversations endpoints (AI Platform)
export const conversations = {
  list: (params) => api.get('/conversations', { params }),
  create: (payload) => api.post('/conversations', payload),
  get: (id, params) => api.get(`/conversations/${id}`, { params }),
  update: (id, payload) => api.put(`/conversations/${id}`, payload),
  delete: (id) => api.delete(`/conversations/${id}`),
  sendMessage: (id, payload) => api.post(`/conversations/${id}/messages`, payload),
  // AgentCore turns stream the response as SSE on the POST itself (there is no
  // ws_url / WebSocket — the gateway proxies the runtime's stream). axios can't
  // read a browser ReadableStream, so this uses fetch and returns the raw
  // Response for the caller to pump. Same base URL + bearer auth as the axios
  // instance; the SM/claude_code path keeps using sendMessage above.
  streamMessage: (id, payload) => {
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
    return fetch(`${API_BASE_URL}/conversations/${id}/messages`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
    });
  },
  // Pre-warm the SM session for a conversation so workspace hydrate completes
  // BEFORE the user types their first message. Idempotent — gateway probes for
  // an alive session and returns it if found, otherwise creates a fresh one.
  // Payload may include `acting_agent_id` so the gateway can enforce the
  // runtime-mismatch guard server-side (returns 409 AGENT_TYPE_MISMATCH).
  warmSession: (id, payload) => api.post(`/conversations/${id}/sessions`, payload || {}),
  stopSession: (id) => api.delete(`/conversations/${id}/sessions`),
};

export const workspaces = {
  list: (params) => api.get('/workspaces', { params }),
  get: (idOrSlug) => api.get(`/workspaces/${idOrSlug}`),
  // Standard chat / non-retell workspace creation. Retell workspaces MUST go
  // through retell.createWorkspace so the kind='retell' invariant holds.
  create: (payload) => api.post('/workspaces', payload),
  createRetell: (payload) => api.post(normalizeRetellApiPath('/voice/retell/workspaces'), payload),
  update: (idOrSlug, payload) => api.put(`/workspaces/${idOrSlug}`, payload),
  getModelRouting: (idOrSlug) => api.get(`/workspaces/${idOrSlug}/model-routing`),
  updateModelRouting: (idOrSlug, payload) => api.put(`/workspaces/${idOrSlug}/model-routing`, payload),
  remove: (idOrSlug, { hard = false } = {}) =>
    api.delete(`/workspaces/${idOrSlug}`, { params: hard ? { hard: 'true' } : undefined }),
  listMcps: (id) => api.get(`/workspaces/${id}/mcps`),
  addMcp: (id, payload) => api.post(`/workspaces/${id}/mcps`, payload),
  removeMcp: (id, mcpId) => api.delete(`/workspaces/${id}/mcps/${mcpId}`),
  listDocs: (id) => api.get(`/workspaces/${id}/docs`),
  readDoc: (id, path) => api.get(`/workspaces/${id}/docs/read`, { params: { path } }),
  saveDocs: (id, payload) => api.post(`/workspaces/${id}/docs`, payload),
  handoffAssets: (id, payload) => api.post(`/workspaces/${id}/assets/handoff`, payload),
  handoffAssetFiles: (id, files, options = {}) => uploadCapabilityAssetsToWorkspace({
    workspaceId: id,
    files,
    ...options,
    handoffAssets: (workspaceId, payload) => api.post(`/workspaces/${workspaceId}/assets/handoff`, payload),
  }),
  deleteDoc: (id, path) => api.delete(`/workspaces/${id}/docs`, { params: { path } }),
  // Re-index failed docs. HTTP issuance only — SM re-queues into the live
  // session and streams progress back over the session WS (output-only-WS
  // model). docNames empty/omitted = retry ALL failed docs. SM 409s if no live
  // session is mounting the workspace.
  reindexDocs: (id, docNames = []) => api.post(`/workspaces/${id}/docs/reindex`, { doc_names: docNames }),
  // Recycle bin (SM manifest): deleted docs stay restorable until the
  // retention sweep. Trash entries come back with path "docs/<rel>" — pass
  // that to restoreDoc verbatim.
  listTrash: (id) => api.get(`/workspaces/${id}/trash`),
  restoreDoc: (id, path) => api.post(`/workspaces/${id}/trash/restore`, { path }),
  moveDoc: (id, oldPath, newPath) => api.put(`/workspaces/${id}/docs/move`, { old_path: oldPath, new_path: newPath }),
  // Stream-download an agent-authored file (rooted at the session cwd, not the
  // docs/ library). Fetches as a blob so the auth header rides along, then
  // triggers a browser download. Resolves to the filename it saved as.
  // Fetch an agent-authored file as a Blob (inline artifact rendering).
  getFileBlob: async (id, path) => {
    const res = await api.get(`/workspaces/${id}/file`, {
      params: { path },
      responseType: 'blob',
    });
    return res.data;
  },
  downloadFile: async (id, path) => {
    const res = await api.get(`/workspaces/${id}/file`, {
      params: { path },
      responseType: 'blob',
    });
    const name = path.split('/').pop() || 'download';
    const url = URL.createObjectURL(res.data);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = name;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
    return name;
  },
};

// Capability marketplace — the REVIEW half. A human attester reads an
// agent-produced readiness report, records corrections, then releases or
// rejects it; earnings are derived from the receipt, never negotiated. Bodies
// and responses on this mount are snake_case (the gateway bypasses its
// camelizer there so an invoke's `arguments` hash stays stable), which is also
// the house style — see CLAUDE.md.
export const marketplaceReview = {
  listQueue: (params) => api.get(marketplaceReviewPaths.queue(), { params }),
  recordCorrection: (executionId, payload) =>
    api.post(marketplaceReviewPaths.corrections(executionId), payload),
  release: (executionId) => api.post(marketplaceReviewPaths.release(executionId)),
  reject: (executionId, payload) => api.post(marketplaceReviewPaths.reject(executionId), payload),
  listEarnings: () => api.get(marketplaceReviewPaths.earnings()),
  listAccessRequests: (params) => api.get(marketplaceReviewPaths.accessRequests(), { params }),
  listPublisherOffers: () => api.get(marketplaceReviewPaths.publisherOffers(), { params: { include_drafts: true } }),
  createPublisherOffer: (payload) => api.post(marketplaceReviewPaths.publisherOffers(), payload),
  updatePublisherOffer: (slug, payload) => api.patch(marketplaceReviewPaths.publisherOffer(slug), payload),
  submitPublisherOffer: (slug) => api.post(marketplaceReviewPaths.submitPublisherOffer(slug)),
  // The report is an ordinary workspace document; reuse the existing read path
  // rather than adding a second way to fetch one.
  readReport: (workspaceId, path) => workspaces.readDoc(workspaceId, path),
};

/*
 * The PUBLIC marketplace surface, behind /use/<slug>.
 *
 * No auth: these gateway routes do not authenticate, and the page that uses
 * them is a marketing page. They ride the shared client anyway so one axios
 * instance still owns the base URL and the error logging; the request
 * interceptor adds a token only when the visitor happens to have one, and the
 * gateway ignores it on this mount.
 */
export const marketplaceOffers = {
  configuration: () => api.get(marketplaceOfferPaths.configuration()),
  manageConfiguration: () => api.get(marketplaceOfferPaths.manageConfiguration()),
  updateConfiguration: (payload) => api.put(marketplaceOfferPaths.configuration(), payload),
  list: () => api.get(marketplaceOfferPaths.offers()),
  get: (slug) => api.get(marketplaceOfferPaths.offer(slug)),
  manifest: (slug) => api.get(marketplaceOfferPaths.manifest(slug)),
  requestAccess: (slug, payload) => api.post(marketplaceOfferPaths.requestAccess(slug), payload),
  invoke: (slug, payload) => api.post(marketplaceOfferPaths.invoke(slug), payload),
  getExecution: (executionId) => api.get(marketplaceOfferPaths.execution(executionId)),
};

// AgentCore — provisioning AgentCore runtimes in a tenant's own AWS account.
// (baseURL already includes /api/v1, so paths are /agentcore/…)
export const agentcore = {
  status: () => api.get('/agentcore/status'),
  validate: (payload) => api.post('/agentcore/validate', payload),
  connect: (payload) => api.post('/agentcore/connect', payload),
  enableTenant: (tenantId) => api.post(`/agentcore/tenants/${tenantId}/enable`),
  disableTenant: (tenantId) => api.post(`/agentcore/tenants/${tenantId}/disable`),
};

// Sessions endpoints (AI Platform)
export const sessions = {
  list: (params) => api.get('/sessions', { params }),
  get: (id) => api.get(`/sessions/${id}`),
  // Kill the sandbox and close the billing row now; the conversation stays.
  end: (id) => api.post(`/sessions/${id}/end`),
  // Tenant admins: end every live session, optionally narrowed.
  endAll: (payload = {}) => api.post('/sessions/end', payload),
};

// Inference invocation history (stateless inference surface)
export const inference = {
  listInvocations: (params) => api.get('/inference/invocations', { params }),
};

// Routing rulebook (Policies) — RBAC is server-side; every row carries
// can_manage and the UI renders from that, never from local role checks.
export const routingRules = {
  list: (params) => api.get('/routing-rules', { params }),
  create: (payload) => api.post('/routing-rules', payload),
  update: (id, payload) => api.put(`/routing-rules/${id}`, payload),
  remove: (id) => api.delete(`/routing-rules/${id}`),
  effective: (params) => api.get('/routing-rules/effective', { params }),
  // Per-tenant execution mode (off | shadow | active) — rides the existing
  // tenant model-routing policy endpoint.
  getTenantMode: (tenantId) => api.get(`/tenants/${tenantId}/model-routing`),
  setTenantMode: (tenantId, mode) => api.put(`/tenants/${tenantId}/model-routing`, { mode }),
};

// Workspace memory categories (platform-admin authoring) + per-tenant default
export const memoryCategories = {
  list: (params) => api.get('/memory-categories', { params }),
  create: (payload) => api.post('/memory-categories', payload),
  update: (id, payload) => api.put(`/memory-categories/${id}`, payload),
  // Replace-set: {scope:'all_tenants'} | {scope:'tenants',tenant_ids} for
  // platform rules; {scope:'all_orgs'} | {scope:'orgs',org_ids} for tenant rules.
  setAssignments: (id, payload) => api.put(`/memory-categories/${id}/assignments`, payload),
  remove: (id) => api.delete(`/memory-categories/${id}`),
  getTenantDefault: (tenantId) => api.get(`/tenants/${tenantId}/memory-default`),
  setTenantDefault: (tenantId, enabled) =>
    api.put(`/tenants/${tenantId}/memory-default`, { memory_default_enabled: enabled }),
};

// Organizations endpoints
export const organizations = {
  // Platform admins may pass { tenant_id } to read another tenant's orgs.
  list: (params) => api.get('/organizations', { params }),
};

// AI Resource Manager - Azure Management endpoints
export const azureManagement = {
  transferBilling: (subscriptionId, newAccountEmail) => 
    api.post('/azure/transfer-billing', { subscriptionId, newAccountEmail }),
  getSubscriptions: () => api.get('/azure/subscriptions'),
  getSubscriptionDetails: (subscriptionId) => api.get(`/azure/subscriptions/${subscriptionId}`),
};

// Master Accounts endpoints
export const masterAccounts = {
  list: () => api.get('/master-accounts'),
  get: (id) => api.get(`/master-accounts/${id}`),
  create: (account) => api.post('/master-accounts', account),
  update: (id, updates) => api.put(`/master-accounts/${id}`, updates),
  delete: (id) => api.delete(`/master-accounts/${id}`),
  refreshCredentials: (id) => api.post(`/master-accounts/${id}/refresh`),
};

// Integration endpoints
export const integrations = {
  getConnectedServices: () => withOptionalFallback(() => api.get('/integrations/connected-services'), { services: [] }),
  connectService: (params) => api.post('/integrations/connect', params),
  disconnectService: (serviceId) => api.delete(`/integrations/disconnect/${serviceId}`),
  getOAuthUrl: (service) => api.get(`/integrations/oauth-url/${service}`),
  handleOAuthCallback: (params) => api.post('/integrations/oauth-callback', params),
  // Token status and refresh endpoints
  getTokenStatus: (tenantId) => withOptionalFallback(() => api.get('/auth/oauth/token-status', {
    headers: { 'X-Tenant-Id': tenantId }
  }), { tokens: [], summary: {} }),
  getServiceTokenStatus: (service, tenantId) => api.get(`/auth/oauth/token-status/${service}`, {
    headers: { 'X-Tenant-Id': tenantId }
  }),
  refreshToken: (service, tenantId) => api.post(`/auth/oauth/refresh/${service}`, {}, {
    headers: { 'X-Tenant-Id': tenantId }
  }),
  getRefreshJobStatus: () => api.get('/auth/oauth/refresh-job/status'),
  triggerRefreshJob: () => api.post('/auth/oauth/refresh-job/run'),
  // Available providers
  getProviders: () => api.get('/auth/oauth/providers'),
};

// Discovery service endpoints
export const discovery = {
  discoverService: (serviceName) => api.post('/discovery/analyze', { serviceName }),
  getServiceInfo: (serviceName) => api.get(`/discovery/service/${serviceName}`),
};

// K8s Infrastructure endpoints (for white-label tenant K8s management)
export const infrastructure = {
  // Get K8s infrastructure status for a tenant (deployments, services, pods, ingresses)
  get: (tenantId) =>
    api.get(`/infrastructure/${tenantId}`, { headers: getTenantApiHeaders() }),

  // Update website settings (env vars in K8s deployment)
  updateSettings: (tenantId, settings) =>
    api.put(`/infrastructure/${tenantId}/website-settings`, { settings }, { headers: getTenantApiHeaders() }),

  // Restart tenant deployments
  restart: (tenantId, deployment = null) =>
    api.post(`/infrastructure/${tenantId}/restart`, { deployment }, { headers: getTenantApiHeaders() }),

  // Scale a deployment
  scale: (tenantId, deployment, replicas) =>
    api.post(`/infrastructure/${tenantId}/scale`, { deployment, replicas }, { headers: getTenantApiHeaders() }),
};

// Terraform orchestration endpoints
export const terraform = {
  createJob: (payload) => api.post('/terraform/jobs', payload, { headers: getTenantApiHeaders() }),
  listJobs: (params) => api.get('/terraform/jobs', { params, headers: getTenantApiHeaders() }),
  getJob: (jobId) => api.get(`/terraform/jobs/${jobId}`, { headers: getTenantApiHeaders() }),
  approveJob: (jobId) => api.post(`/terraform/jobs/${jobId}/approve`, {}, { headers: getTenantApiHeaders() }),
  applyJob: (jobId, approved = false) =>
    api.post(`/terraform/jobs/${jobId}/apply`, { approved }, { headers: getTenantApiHeaders() }),
  getLogs: (jobId) => api.get(`/terraform/jobs/${jobId}/logs`, { headers: getTenantApiHeaders() }),
  getOutputs: (jobId) => api.get(`/terraform/jobs/${jobId}/outputs`, { headers: getTenantApiHeaders() }),
  infraStatus: (tenantId) =>
    api.get(`/terraform/infra/${tenantId}`, { headers: getTenantApiHeaders() })
};


// Compute execution endpoints
export const compute = {
  listWorkspaceBindings: workspaceId => api.get(`/compute/workspaces/${encodeURIComponent(workspaceId)}/bindings`, { headers: getTenantApiHeaders() }),
  listWorkspaces: (params = {}) => api.get('/compute/workspaces', { params, headers: getTenantApiHeaders() }),
  instantRun: (token, payload) => api.post('/compute/instant-run', payload, {
    headers: { 'X-Compute-Token': token }
  })
};

// Tenant Request endpoints (white-label provisioning workflow)
export const tenantRequests = {
  // Public - Submit a new tenant request
  create: (payload) => api.post('/tenant-requests', payload),

  // Public - Get request status by ID
  get: (requestId) => api.get(`/tenant-requests/${requestId}`),

  // Public - Check slug availability
  checkSlug: (slug) => api.get(`/tenant-requests/by-slug/${slug}`),

  // Admin - List all requests
  list: (params) => api.get('/admin/tenant-requests', { params, headers: getTenantApiHeaders() }),

  // Admin - Get full request details
  getAdmin: (requestId) => api.get(`/admin/tenant-requests/${requestId}`, { headers: getTenantApiHeaders() }),

  // Admin - Approve and activate request (creates tenant immediately)
  approve: (requestId, { notes = null, customDomain = null } = {}) =>
    api.post(`/admin/tenant-requests/${requestId}/approve`, { notes, customDomain }, { headers: getTenantApiHeaders() }),

  // Admin - Reject request
  reject: (requestId, reason) =>
    api.post(`/admin/tenant-requests/${requestId}/reject`, { reason }, { headers: getTenantApiHeaders() }),
};

// ───────────────────────────────────────────────────────────────────────────
// Workflows — uTask integration surface (templates, runs, approvals, events).
//
// CASING EXCEPTION (read carefully — differs from the rest of this file):
//   • Responses are snake_case like everything else (gateway snakifies all
//     /api/v1/* responses), so READ snake_case: run.steps_done,
//     run.utask_task_id, run.resolution.run_count, step.action_type, etc.
//   • Request BODIES are NOT camelized by the gateway for the /workflows
//     subtree (it's excluded so opaque uTask payloads pass through verbatim).
//     The route handlers read camelCase wrapper keys directly, so we MUST send
//     `resolverInputs` and `commonInput` in camelCase — NOT snake_case. Single
//     word keys (input, tags, yaml, name, content, state, steps, inputs…) are
//     unaffected. Opaque blobs (step objects, callback bodies) go verbatim.
//   • Query params stay snake_case: template_id, batch_id, resource_type,
//     resource_id, run_id, status, state, search, limit, offset, after, before.
// ───────────────────────────────────────────────────────────────────────────
export const workflows = {
  // Draft authoring (agent/UI-safe layer above raw uTask YAML)
  planActions: (payload) => api.post('/workflows/drafts/plan-actions', payload),
  validateDraft: (draft) => api.post('/workflows/drafts/validate', { draft }),
  compileDraft: (draft) => api.post('/workflows/drafts/compile', { draft }),

  // Templates (the workflow definitions)
  listTemplates: (params) => api.get('/workflows/templates', { params }),
  getTemplate: (id) => api.get(`/workflows/templates/${id}`),
  createTemplate: (payload) => api.post('/workflows/templates', payload), // { name, description, yaml }
  createTemplateFromActionPlan: (payload) => api.post('/workflows/templates/from-action-plan', payload),
  createTemplateFromReviewedActionPlan: (input) => api.post('/workflows/templates/from-action-plan', buildCreateWorkflowFromActionPlanPayload(input)),
  updateTemplate: (id, payload) => api.put(`/workflows/templates/${id}`, payload),
  deleteTemplate: (id) => api.delete(`/workflows/templates/${id}`),
  getModelRouting: (id) => api.get(`/workflows/templates/${id}/model-routing`),
  updateModelRouting: (id, payload) => api.put(`/workflows/templates/${id}/model-routing`, payload),
  getStepModelRouting: (id, stepId) => api.get(`/workflows/templates/${id}/steps/${encodeURIComponent(stepId)}/model-routing`),
  updateStepModelRouting: (id, stepId, payload) => api.put(`/workflows/templates/${id}/steps/${encodeURIComponent(stepId)}/model-routing`, payload),
  previewTemplate: (id, payload) => api.post(`/workflows/templates/${id}/preview`, payload),
  previewActionPlanTemplate: (id, input) => api.post(`/workflows/templates/${id}/preview`, buildWorkflowActionPlanExecutionPayload(input)),
  // runTemplate body: { input, delay?, resolverInputs? } — resolverInputs is camelCase on purpose
  runTemplate: (id, payload) => api.post(`/workflows/templates/${id}/run`, payload),
  runActionPlanTemplate: (id, input) => api.post(`/workflows/templates/${id}/run`, buildWorkflowActionPlanExecutionPayload(input)),
  // runBatch body: { inputs: [...], commonInput?, comment? } — commonInput is camelCase on purpose
  runBatch: (id, payload) => api.post(`/workflows/templates/${id}/batch`, payload),

  // Debug mode (§14) — interactive step-through verification before publishing.
  // debugStart body: { input, workspace_id }. Responses are snake_case:
  // { run_id, state, paused, current_step, current_output, current_error, steps }.
  debugStart: (id, payload) => api.post(`/workflows/templates/${id}/debug/start`, payload),
  debugState: (runId) => api.get(`/workflows/runs/${runId}/debug/state`),
  debugContinue: (runId) => api.post(`/workflows/runs/${runId}/debug/continue`),
  debugAbort: (runId) => api.post(`/workflows/runs/${runId}/debug/abort`),
  publishWorkflow: (id) => api.post(`/workflows/templates/${id}/publish`),
  // Debug runs history for the Debug tab. Each row is snake_case + carries
  // workflow_name + real_template_id (for re-run). The Runs tab's listRuns EXCLUDES these.
  listDebugRuns: (params) => api.get('/workflows/debug/runs', { params }),

  // Workspaces — for the run-launcher picker (a run resolves its MCPs from a workspace)
  listWorkspaces: () => api.get('/workspaces'),

  // Schedules (recurring runs). A scheduled run is a normal run fired by the
  // in-gateway ticker at cron times. Only published workflows are schedulable.
  listSchedules: (templateId) => api.get(`/workflows/templates/${templateId}/schedules`),
  createSchedule: (templateId, payload) => api.post(`/workflows/templates/${templateId}/schedules`, payload),
  updateSchedule: (scheduleId, payload) => api.put(`/workflows/schedules/${scheduleId}`, payload),
  deleteSchedule: (scheduleId) => api.delete(`/workflows/schedules/${scheduleId}`),

  // Runs
  listRuns: (params) => api.get('/workflows/runs', { params }),
  stats: () => api.get('/workflows/stats'),
  getRun: (id) => api.get(`/workflows/runs/${id}`), // includes the per-step resolution DAG
  editRun: (id, payload) => api.put(`/workflows/runs/${id}`, payload), // { input?, tags? } (paused only)
  deleteRun: (id) => api.delete(`/workflows/runs/${id}`), // manager-gated
  cancelRun: (id) => api.post(`/workflows/runs/${id}/cancel`),
  resumeRun: (id) => api.post(`/workflows/runs/${id}/run`),
  pauseRun: (id) => api.post(`/workflows/runs/${id}/pause`),
  extendRun: (id) => api.post(`/workflows/runs/${id}/extend`),
  startOverRun: (id) => api.post(`/workflows/runs/${id}/start-over`),
  getRunStep: (id, stepName) =>
    api.get(`/workflows/runs/${id}/steps/${encodeURIComponent(stepName)}`),
  editStep: (id, stepName, step) =>
    api.put(`/workflows/runs/${id}/steps/${encodeURIComponent(stepName)}`, step),
  editStepState: (id, stepName, state) =>
    api.put(`/workflows/runs/${id}/steps/${encodeURIComponent(stepName)}/state`, { state }),
  editResolution: (id, payload) => api.put(`/workflows/runs/${id}/resolution`, payload), // { steps?, resolverInputs? }

  // Run comments (gateway-owned)
  listComments: (runId) => api.get(`/workflows/runs/${runId}/comments`),
  addComment: (runId, content) => api.post(`/workflows/runs/${runId}/comments`, { content }),
  editComment: (runId, commentId, content) =>
    api.put(`/workflows/runs/${runId}/comments/${commentId}`, { content }),
  deleteComment: (runId, commentId) =>
    api.delete(`/workflows/runs/${runId}/comments/${commentId}`),

  // Run resolution attempt history
  listRunResolutions: (runId) => api.get(`/workflows/runs/${runId}/resolutions`),

  // Approvals (projected uTask callback gates)
  listApprovals: (params) => api.get('/workflows/approvals', { params }), // { mine?, status? }
  listRunApprovals: (runId) => api.get(`/workflows/runs/${runId}/approvals`),
  approve: (approvalId, payload) => api.post(`/workflows/approvals/${approvalId}/approve`, payload || {}),
  reject: (approvalId, payload) => api.post(`/workflows/approvals/${approvalId}/reject`, payload || {}),

  // Engine events feed + audit trail
  listEvents: (params) => api.get('/workflows/events', { params }), // { run_id? }
  listAudit: (params) => api.get('/workflows/audit', { params }), // { resource_type?, resource_id? }

  // Functions (base builtins + scoped dynamic)
  listFunctions: () => api.get('/workflows/functions'),
  getFunction: (id) => api.get(`/workflows/functions/${encodeURIComponent(id)}`),
  createFunction: (payload) => api.post('/workflows/functions', payload), // { name?, description?, yaml }
  updateFunction: (id, payload) => api.put(`/workflows/functions/${encodeURIComponent(id)}`, payload),
  deleteFunction: (id) => api.delete(`/workflows/functions/${encodeURIComponent(id)}`),
};

// ============================================
// PLATFORM ADMIN — user management + API keys
// All admin/user-session (JWT) operations; the gateway blocks api_key auth on
// these routes (requireTenantAdmin / platform-admin). snake_case wire format.
// ============================================
export const adminUsers = {
  // Platform-admin cross-tenant users console. Returns { users, total, limit, offset }
  // with each user's tenant attached, plus per-user activity stats
  // (login_count, order_count, last_order_at, has_integration_access).
  // params: { search, tenant_id, role, has_integrations, sort, dir, limit, offset }.
  list: (params = {}) =>
    api.get('/admin/users', { params, headers: getUserApiHeaders() }),
  // A single user (any tenant), with its tenant attached: { user }.
  get: (id) =>
    api.get(`/admin/users/${id}`, { headers: getUserApiHeaders() }),
  // Distinct tenants (incl. the platform tenant) for the filter dropdown.
  tenants: () =>
    api.get('/admin/users/tenants', { headers: getUserApiHeaders() }),
};

// Platform-admin tenant feature grants (AgentCore, compliance workspaces).
// Granting is a commercial act, so these are platform-admin + user-session
// only — the gateway rejects api_key auth on them.
export const tenantFeatures = {
  // { features: [{key,label,description,caveat}], tenants: [{id,slug,name,is_platform,features}] }
  list: () => api.get('/admin/tenant-features', { headers: getUserApiHeaders() }),
  // Grant or revoke. Idempotent — the response carries { changed } so the UI
  // can tell a real transition from a no-op.
  set: (tenantId, feature, enabled) =>
    api.put(`/admin/tenant-features/${tenantId}/${feature}`, { enabled },
      { headers: getUserApiHeaders() }),
};

// Platform-admin release controls. The gateway owns the GitHub credential and
// enforces repository/branch/workflow allowlists; the browser never receives a
// token and every PR mutation is tied to an exact head SHA.
export const releaseManagement = {
  overview: () => api.get('/admin/releases', { headers: getUserApiHeaders() }),
  repository: (repository) =>
    api.get(`/admin/releases/${encodeURIComponent(repository)}`, { headers: getUserApiHeaders() }),
  applyProtection: (repository, branch, requiredApprovingReviewCount = 0) =>
    api.put(
      `/admin/releases/${encodeURIComponent(repository)}/branches/${encodeURIComponent(branch)}/protection`,
      { required_approving_review_count: requiredApprovingReviewCount },
      { headers: getUserApiHeaders() },
    ),
  dispatchWorkflow: (repository, workflow, ref) =>
    api.post(
      `/admin/releases/${encodeURIComponent(repository)}/workflows/${encodeURIComponent(workflow)}/dispatch`,
      { ref },
      { headers: getUserApiHeaders() },
    ),
  updatePullRequestBranch: (repository, pullNumber, expectedHeadSha) =>
    api.post(
      `/admin/releases/${encodeURIComponent(repository)}/pulls/${pullNumber}/update-branch`,
      { expected_head_sha: expectedHeadSha },
      { headers: getUserApiHeaders() },
    ),
  mergePullRequest: (repository, pullNumber, expectedHeadSha) =>
    api.post(
      `/admin/releases/${encodeURIComponent(repository)}/pulls/${pullNumber}/merge`,
      { expected_head_sha: expectedHeadSha },
      { headers: getUserApiHeaders() },
    ),
};

export const adminIntegrations = {
  // Platform-admin cross-tenant integrations console. Returns
  // { integrations, total, limit, offset } — one row per tenant_mcps
  // enablement with tenant + catalog info and connected_user_count attached.
  // params: { tenant_id, search, status, sort, dir, limit, offset }.
  // Tenant filter dropdown: reuse adminUsers.tenants(), not a new endpoint.
  list: (params = {}) =>
    api.get('/admin/integrations', { params, headers: getUserApiHeaders() }),
};

export const apiKeys = {
  // The tenant's single tenant-subject key (unchanged legacy shape: { key }).
  getTenantKey: () => api.get('/tenant-api-keys', { headers: getUserApiHeaders() }),
  // All keys (tenant + user subjects): { keys: [...] }. A platform admin may pass
  // { tenant_id } to read another tenant's keys.
  listAll: (params = {}) => api.get('/tenant-api-keys/keys', { params, headers: getUserApiHeaders() }),
  // Create a key. For a user-subject key (platform-admin only):
  //   { subject_type: 'user', subject_id: <userId>, permissions: [...] }
  // `tenant_id` names the tenant to mint into — platform-admin only; defaults to
  // the caller's own tenant when omitted.
  // The full `api_key` is returned ONCE in the response.
  create: (payload) => api.post('/tenant-api-keys', payload, { headers: getUserApiHeaders() }),
  // Rotate; revoke.
  regenerate: (payload) => api.post('/tenant-api-keys/regenerate', payload, { headers: getUserApiHeaders() }),
  // `tenant_id` must match the tenant the key was listed under, or the revoke
  // matches no row — a platform admin revoking another tenant's key needs it.
  revoke: (id, params = {}) => api.delete(`/tenant-api-keys/${id}`, { params, headers: getUserApiHeaders() }),
};

// Human browser supervision remains bound to the exact Gateway agent-plan run.
// Tenant, project, provider account, and browser-session authority are derived
// server-side from that run and must never be accepted from the browser client.
function takeoverPath(agentPlanId, runId, suffix = '') {
  return `/agent-plans/${encodeURIComponent(agentPlanId)}/runs/${encodeURIComponent(runId)}/computer-use/takeover${suffix}`;
}

function idempotencyHeaders(idempotencyKey) {
  return { headers: { 'Idempotency-Key': idempotencyKey } };
}

export const agentPlanBrowserTakeover = {
  start: (agentPlanId, runId, idempotencyKey) =>
    api.post(takeoverPath(agentPlanId, runId, '/start'), {}, idempotencyHeaders(idempotencyKey)),
  status: (agentPlanId, runId) => api.get(takeoverPath(agentPlanId, runId)),
  frame: (agentPlanId, runId) => api.get(takeoverPath(agentPlanId, runId, '/frame')),
  action: (agentPlanId, runId, action, idempotencyKey) =>
    api.post(takeoverPath(agentPlanId, runId, '/actions'), action, idempotencyHeaders(idempotencyKey)),
  complete: (agentPlanId, runId, idempotencyKey) =>
    api.post(takeoverPath(agentPlanId, runId, '/complete'), {}, idempotencyHeaders(idempotencyKey)),
  stop: (agentPlanId, runId, idempotencyKey) =>
    api.delete(takeoverPath(agentPlanId, runId), idempotencyHeaders(idempotencyKey)),
  resumeCompleted: (agentPlanId, runId, dashboardCaptureSessionId) =>
    api.post(`/agent-plans/${encodeURIComponent(agentPlanId)}/runs/${encodeURIComponent(runId)}/resume`, {
      human_takeover_completed: true,
      dashboard_capture_session_id: dashboardCaptureSessionId,
    }),
};

export default api;
